import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BoxLabelPreview from "@/components/BoxLabelPreview";
import CameraCapture from "@/components/CameraCapture";
import { LabelPhotoStrip } from "@/components/PhotoModal";
import MaterialCombobox from "@/components/MaterialCombobox";
import { PageHeader, Table, Btn, Modal, Input, Select, Badge, Card, Textarea } from "@/components/UI";
import { Plus, Download, FileText, Bell as BellIcon, Trash2, ShieldAlert, MessageCircle, Send, RefreshCw, ShieldCheck, Archive, Upload, KeyRound, ShoppingCart, Mail, Tag, AlertTriangle, Camera, Loader2, ImageIcon, HardDrive, Cloud, CheckCircle2, FlaskConical } from "lucide-react";
import SecurityMFA from "@/components/SecurityMFA";
import { FilterAndReportBar } from "@/components/FilterAndReportBar";
import MeetingsBanner from "@/components/MeetingsBanner";
import TrDateInput from "@/components/TrDateInput";
import { usePagination, ListPagination } from "@/components/ListPagination";
import { doneLast } from "@/lib/listOrder";
import { todayTR } from "@/lib/date";

// ---------- PO Gönderim (Telegram + Email) buttons ----------
function PoTelegramBtn({ po, suppliers, onSent }) {
  const [busy, setBusy] = React.useState(false);
  const sup = suppliers.find(s => s.name === po.supplier);
  const hasChat = !!sup?.telegram_chat_id;
  const canSend = ["ordered", "partial_received", "received"].includes(po.status);
  const disabled = !canSend || busy;
  const title = !canSend ? "Satın Alma tedarikçiye gönderilmeden önce onaylanmalıdır."
    : !hasChat ? "Bu tedarikçinin Telegram bağlantısı bulunmamaktadır."
    : "Telegram ile PDF Gönder";
  const send = async () => {
    if (!canSend) { alert("Satın Alma tedarikçiye gönderilmeden önce onaylanmalıdır."); return; }
    if (!hasChat) { alert("Bu tedarikçinin Telegram bağlantısı bulunmamaktadır."); return; }
    setBusy(true);
    try { await api.post(`/purchase-orders/${po.id}/send-telegram`, {}); alert(`✓ Telegram ile gönderildi: ${po.po_no}`); onSent?.(); }
    catch (e) { alert(e.response?.data?.detail || "Gönderim başarısız."); }
    finally { setBusy(false); }
  };
  return (
    <Btn size="sm" variant="secondary" disabled={disabled} onClick={send} title={title} data-testid={`po-tg-${po.id}`}>
      <Send size={12}/>
    </Btn>
  );
}
function PoEmailBtn({ po, suppliers, onSent }) {
  const [busy, setBusy] = React.useState(false);
  const [emailReady, setEmailReady] = React.useState(null);  // null=unknown, true/false=known
  React.useEffect(() => {
    if (emailReady !== null) return;
    api.get("/system/email-config").then(r => setEmailReady(!!r.configured)).catch(() => setEmailReady(false));
  }, [emailReady]);
  const sup = suppliers.find(s => s.name === po.supplier);
  const hasEmail = !!sup?.email;
  const canSend = ["ordered", "partial_received", "received"].includes(po.status);
  const disabled = !canSend || busy;
  const title = !canSend ? "Satın Alma tedarikçiye gönderilmeden önce onaylanmalıdır."
    : !hasEmail ? "Bu tedarikçinin kayıtlı e-posta adresi bulunmamaktadır."
    : emailReady === false ? "E-posta servisi henüz yapılandırılmamıştır. Sistem Ayarlarından e-posta bağlantısını tamamlayınız."
    : "E-posta ile PDF Gönder";
  const send = async () => {
    if (!canSend) { alert("Satın Alma tedarikçiye gönderilmeden önce onaylanmalıdır."); return; }
    if (!hasEmail) { alert("Bu tedarikçinin kayıtlı e-posta adresi bulunmamaktadır."); return; }
    if (emailReady === false) { alert("E-posta servisi henüz yapılandırılmamıştır. Sistem Ayarlarından e-posta bağlantısını tamamlayınız."); return; }
    setBusy(true);
    try { await api.post(`/purchase-orders/${po.id}/send-email`, {}); alert(`✓ E-posta ile gönderildi: ${po.po_no} → ${sup.email}`); onSent?.(); }
    catch (e) { alert(e.response?.data?.detail || "E-posta gönderilemedi."); }
    finally { setBusy(false); }
  };
  return (
    <Btn size="sm" variant="secondary" disabled={disabled} onClick={send} title={title} data-testid={`po-email-${po.id}`}>
      <Mail size={12}/>
    </Btn>
  );
}

export function PurchasingPage() {
  const { user, isAdmin, isSystemAuthority } = useAuth();
  const canCompletePO = isAdmin || isSystemAuthority;
  const [reqs, setReqs] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [critical, setCritical] = useState([]);
  const [pos, setPos] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // iter272 — URL param destekli tab: /purchasing?tab=needs opens ihtiyac listesi
  const _initialTab = (() => {
    try { return new URLSearchParams(window.location.search).get("tab") || "pos"; }
    catch { return "pos"; }
  })();
  const [tab, setTab] = useState(_initialTab);
  const [needsFilter, setNeedsFilter] = useState({ q: "", priority: "" });
  // iter273 — Toplu sipariş için seçili malzeme ID'leri
  const [needsSelected, setNeedsSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [receiveOpen, setReceiveOpen] = useState(null);
  const [qcQueue, setQcQueue] = useState(null);
  const [rec, setRec] = useState({ received_by_name: "", received_at: "", notes: "", lines: [] });
  const [ocrLine, setOcrLine] = useState(null); // {idx, busy: true}
  const [pendingLabelPhotos, setPendingLabelPhotos] = useState([]); // [{photo_id, material_id}]
  const [cameraOpenIdx, setCameraOpenIdx] = useState(null); // hangi satır için kamera açık
  const [historyOpen, setHistoryOpen] = useState(null);
  const [filteredPos, setFilteredPos] = useState([]);
  // [iter72] Etiket Yazdır (msg-93 Bölüm A) — malzeme seçim modalı + preview
  const [labelPickPo, setLabelPickPo] = useState(null); // { po, lines: [{material_id, lots: []}] }
  const [labelPreview, setLabelPreview] = useState(null); // { materialId, lotId, poId, labelKind }
  // [iter96] Fix Pkg 02 Bölüm C — Yeni Envanter modal state (PO create/edit sırasında açılan inline modal)
  const [newMatModal, setNewMatModal] = useState(null); // { lineIdx, prefillName, form:{...} } | null
  const [newMatBusy, setNewMatBusy] = useState(false);
  const empty = { supplier: "", expected_delivery: "", lines: [], priority: "normal" };
  const [f, setF] = useState(empty);
  const load = async () => {
    // Defensive: partial fail should not blank out the whole page.
    // Each endpoint isolated so DB wipes/permission drops leave the page usable.
    const safe = async (url) => { try { return await api.get(url); } catch (_) { return []; } };
    const [r, n, c, p, m, s] = await Promise.all([
      safe("/purchasing-critical-shortage"),
      safe("/purchasing/material-needs"),
      safe("/purchasing/critical-stock-strict"),
      safe("/purchase-orders"),
      safe("/materials"),
      safe("/suppliers"),
    ]);
    setReqs(Array.isArray(r) ? r : []);
    setNeeds(Array.isArray(n) ? n : []);
    setCritical(Array.isArray(c) ? c : []);
    setPos(Array.isArray(p) ? p : []);
    setMaterials(Array.isArray(m) ? m : []);
    setSuppliers(Array.isArray(s) ? s : []);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing) await api.put(`/purchase-orders/${editing.id}`, f);
      else await api.post("/purchase-orders", f);
      setOpen(false); setEditing(null); setF(empty); load();
    } catch (e) { alert(e.response?.data?.detail); }
  };
  const approve = async (id) => { await api.post(`/purchase-orders/${id}/approve`); load(); };
  const cancel = async (id, po_no) => {
    const reason = window.prompt(`${po_no || "PO"} iptal sebebi (opsiyonel):`, "");
    if (reason === null) return;
    try { await api.post(`/purchase-orders/${id}/cancel`, { reason }); load(); }
    catch (e) { alert(e.response?.data?.detail || "İptal edilemedi"); }
  };
  const del = async (id) => { if (window.confirm("Silinsin/iptal edilsin mi?")) { try { await api.del(`/purchase-orders/${id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };
  // iter312 — Admin/System Authority partial PO'yu teslim stoğuna hiç dokunmadan kapatabilir.
  const completePO = async (po) => {
    const reason = window.prompt(
      `${po.po_no} eksik kalan miktar stok oluşturmadan kapatılacak. Not (opsiyonel):`, "");
    if (reason === null) return;
    try {
      await api.post(`/purchase-orders/${po.id}/complete`, { reason: reason || "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Kapatılamadı"); }
  };
  const openEdit = (r) => { setEditing(r); setF({ ...empty, ...r }); setOpen(true); };
  const pgReqs = usePagination(reqs, "purchasing-reqs", 100);
  const pgCritical = usePagination(critical, "purchasing-critical", 100);
  // iter272 — Filtered needs (search + priority) with pagination.
  const needsFiltered = React.useMemo(() => {
    const q = (needsFilter.q || "").toLowerCase().trim();
    return (needs || []).filter(r => {
      if (needsFilter.priority && r.priority !== needsFilter.priority) return false;
      if (!q) return true;
      const hay = [r.material_name, r.material_code,
                   ...(r.sales_orders || []).map(s => s.so_no || ""),
                   ...(r.sales_orders || []).map(s => s.customer_name || "")].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [needs, needsFilter]);
  const pgNeeds = usePagination(needsFiltered, "purchasing-needs", 100);
  const _posList = doneLast(filteredPos.length > 0 || pos.length === 0 ? filteredPos : pos,
    r => r.status === "received" || r.status === "completed",
    r => r.manually_completed_at || r.last_received_at || r.updated_at);
  const pgPos = usePagination(_posList, "purchasing-pos", 100);

  // iter272 — Priority badge helper (Acil / Normal / Düşük) — SO'dan gelen renk kodu.
  const PRIO_BADGE = {
    urgent: "bg-red-100 text-red-700 border-red-300",
    normal: "bg-slate-100 text-slate-700 border-slate-300",
    low: "bg-amber-50 text-amber-700 border-amber-200",
  };
  const PRIO_LABEL = { urgent: "Acil", normal: "Normal", low: "Düşük" };
  const prioBadge = (p) => (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${PRIO_BADGE[p] || PRIO_BADGE.normal}`}>{PRIO_LABEL[p] || p}</span>
  );
  // iter272 — Hızlı sipariş oluştur (İhtiyaç veya Kritik Stok satırından)
  const quickCreateFromNeed = (r) => {
    // r shape'i needs endpoint'inden: material_id, unit, preferred_supplier_name, priority, net_shortage, sales_orders[]
    setEditing(null);
    setF({
      supplier: r.preferred_supplier_name || "",
      expected_delivery: "",
      priority: r.priority || "normal",
      linked_so_id: (r.sales_orders && r.sales_orders[0]?.so_id) || null,
      linked_so_no: (r.sales_orders && r.sales_orders[0]?.so_no) || null,
      lines: [{ material_id: r.material_id, qty: r.net_shortage, unit: r.unit || "kg", unit_price: 0 }],
    });
    setOpen(true);
  };
  const quickCreateFromCritical = (r) => {
    setEditing(null);
    // Kritik listede satış siparişi bağlantısı yok → varsayılan Normal, ama aynı malzeme
    // İhtiyaç Listesi'nde Acil olarak varsa Acil'e yükseltelim (spec §7 kuralı).
    const soLink = (needs || []).find(n => n.material_id === r.material_id);
    const priority = soLink?.priority || "normal";
    setF({
      supplier: r.preferred_supplier_name || "",
      expected_delivery: "",
      priority,
      linked_so_id: soLink?.sales_orders?.[0]?.so_id || null,
      linked_so_no: soLink?.sales_orders?.[0]?.so_no || null,
      lines: [{ material_id: r.material_id, qty: r.shortage_to_critical, unit: r.unit || "kg", unit_price: 0 }],
    });
    setOpen(true);
  };

  // [iter72] Etiket Yazdır — sadece teslim alınmış kalemleri listele. Her satır için
  // material.unit kg ise lot seçimi, adet ise direkt açılır. Read-only (spec 45).
  const openLabelPicker = async (po) => {
    // Sadece teslim alınmış satırlar
    const receivedLines = (po.lines || []).filter(ln => (parseFloat(ln.received_qty) || 0) > 0.0001);
    if (receivedLines.length === 0) {
      alert("Bu satın alma emrinde henüz teslim alınmış kalem yok.");
      return;
    }
    // KG satırları için lot listesini yükle
    const enriched = await Promise.all(receivedLines.map(async (ln) => {
      const mat = materials.find(m => m.id === ln.material_id) || {};
      const isKg = (mat.unit || "").toLowerCase() !== "adet" && (mat.unit || "").toLowerCase() !== "pcs";
      let lots = [];
      if (isKg) {
        try { lots = await api.get(`/material-labels/${ln.material_id}/lots`); } catch (_) { lots = []; }
        // Bu PO'ya ait lot'ları öne al
        lots = (Array.isArray(lots) ? lots : []).sort((a, b) => {
          const av = a.purchase_order_id === po.id ? -1 : 1;
          const bv = b.purchase_order_id === po.id ? -1 : 1;
          return av - bv;
        });
      }
      return { ...ln, material_name: mat.name, material_code: mat.code, unit: mat.unit, is_kg: isKg, lots };
    }));
    setLabelPickPo({ po, lines: enriched });
  };

  const pickLabel = (line, lotId) => {
    setLabelPreview({
      materialId: line.material_id,
      lotId: line.is_kg ? lotId : null,
      poId: labelPickPo.po.id,
      labelKind: line.is_kg ? "KG" : "ADET",
    });
  };
  const remainingQty = (r, ln) => Math.max(0, (parseFloat(ln.qty) || 0) - (parseFloat(ln.received_qty) || 0));
  const isFullyReceived = (r) => (r.lines || []).length > 0 && r.lines.every(ln => remainingQty(r, ln) <= 0.0001);
  const openReceive = (r) => {
    const lines = (r.lines || [])
      .map((ln, idx) => ({ idx, material_id: ln.material_id, remaining: remainingQty(r, ln), unit: ln.unit }))
      .filter(l => l.remaining > 0.0001)
      .map(l => ({ ...l, selected: false, qty: l.remaining, lot_no: "", mfg_date: "", expiry_date: "" }));
    setReceiveOpen(r);
    setRec({ received_by_name: user?.name || "", received_at: todayTR(), notes: "", lines });
  };
  const updLine = (i, k, v) => {
    const arr = [...rec.lines];
    arr[i] = { ...arr[i], [k]: v };
    // Manuel düzeltme: OCR uyarısını temizle ve manuel flag'i set et
    if (["lot_no", "mfg_date", "expiry_date"].includes(k)) {
      arr[i][`${k}_user_edited`] = true;
      if (arr[i].confidence?.[k] === "low") {
        arr[i] = { ...arr[i], confidence: { ...arr[i].confidence, [k]: "high" } };
      }
    }
    setRec({ ...rec, lines: arr });
  };
  const _apply_ocr_to_line = (idx, ocr, photoId) => {
    const arr = [...rec.lines];
    const l = { ...arr[idx] };
    l.confidence = { ...(l.confidence || {}), ...ocr.confidence };
    // Sessiz overwrite yok — kullanıcı manuel dokunmadıysa doldur.
    const applyField = (k) => {
      const v = ocr[k];
      if (!v) return;
      if (!l[`${k}_user_edited`]) {
        // Tarih ise YYYY-MM-DD forma indir (partial YYYY-MM ise kabul et)
        l[k] = v.length === 10 ? v : v; // TrDateInput ISO date bekliyor
      } else if (l[k] !== v) {
        // Manuel değere dokunmuyoruz; kullanıcıya sinyal olarak alternate suggestion sakla
        l[`${k}_suggested`] = v;
      }
    };
    applyField("lot_no");
    applyField("mfg_date");
    applyField("expiry_date");
    l.selected = true;
    l.label_photo_ids = [...(l.label_photo_ids || []), photoId];
    arr[idx] = l;
    setRec({ ...rec, lines: arr });
  };
  const runOcr = async (idx, file) => {
    if (!file) return;
    setOcrLine({ idx, busy: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("save", "1");
      fd.append("ref_type", "purchase_order");
      fd.append("ref_id", receiveOpen?.id || "");
      const token = localStorage.getItem("erp_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/photos/ocr/supplier-label`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || `OCR başarısız: ${res.status}`);
        return;
      }
      const data = await res.json();
      const ocr = data.ocr || {};
      const photoId = data.photo_id;
      if (photoId) {
        const l = rec.lines[idx];
        setPendingLabelPhotos(p => [...p, { photo_id: photoId, material_id: l?.material_id }]);
      }
      _apply_ocr_to_line(idx, ocr, photoId);
    } catch (e) {
      alert("Etiket okunamadı. Bilgileri elle girebilirsiniz. (" + e.message + ")");
    } finally { setOcrLine(null); }
  };
  const doReceive = async () => {
    if (!rec.received_by_name.trim()) { alert("Teslim alan kişi zorunlu"); return; }
    const selected = rec.lines.filter(l => l.selected);
    if (selected.length === 0) { alert("En az bir kalem seçmelisiniz."); return; }
    const isKgUnit = u => ["kg","g","l","ml"].includes((u||"kg").toLowerCase());
    for (const l of selected) {
      if (isKgUnit(l.unit)) {
        if (!l.lot_no.trim() || !l.mfg_date || !l.expiry_date) {
          alert(`${mname(l.material_id)} (kg birimli): Lot No, Üretim Tarihi ve SKT zorunludur.`); return;
        }
      }
      if (!l.qty || parseFloat(l.qty) <= 0) { alert(`${mname(l.material_id)}: Miktar 0'dan büyük olmalı.`); return; }
      // iter312 — Over-receive EXPLICITLY ALLOWED. Only warn, do not block.
      if (parseFloat(l.qty) > l.remaining + 0.0001) {
        const over = (parseFloat(l.qty) - l.remaining).toFixed(2);
        const ok = window.confirm(`${mname(l.material_id)}: Siparişten ${over} ${l.unit} fazla teslim alınacak. Devam edilsin mi?`);
        if (!ok) return;
      }
    }
    try {
      const payload = {
        received_by_name: rec.received_by_name.trim(),
        received_at: rec.received_at ? new Date(rec.received_at).toISOString() : undefined,
        notes: rec.notes,
        lines: selected.map(l => ({
          material_id: l.material_id,
          qty: parseFloat(l.qty),
          unit: l.unit || "kg",
          lot_no: (l.lot_no || "").trim() || null,
          mfg_date: l.mfg_date || null,
          expiry_date: l.expiry_date || null,
        })),
      };
      const r = await api.post(`/purchase-orders/${receiveOpen.id}/receive`, payload);
      // OCR üzerinden gelen supplier label fotoğraflarını gerçek inventory_lot ID'lerine bağla
      try {
        const items = r.delivery?.items || [];
        for (const it of items) {
          const linesForMat = rec.lines.filter(l => l.material_id === it.material_id && (l.label_photo_ids || []).length > 0);
          const photoIds = linesForMat.flatMap(l => l.label_photo_ids || []);
          if (photoIds.length && it.lot_id) {
            await api.post("/photos/link", {
              photo_ids: photoIds,
              new_context_type: "SUPPLIER_LABEL",
              new_ref_type: "inventory_lot",
              new_ref_id: it.lot_id,
            });
          }
        }
      } catch { /* non-critical */ }
      setReceiveOpen(null); load();
      // If any kg-unit lots were created, open QC popup queue for them
      const isKgUnit = u => ["kg","g","l","ml"].includes((u||"kg").toLowerCase());
      const qcLots = (r.delivery?.items || []).filter(it => isKgUnit(it.unit)).map(it => ({
        lot_id: it.lot_id, lot_code: it.lot_code,
        material_id: it.material_id, material_name: it.material_name,
        supplier_lot: it.lot_no || "", mfg_date: it.mfg_date, expiry_date: it.expiry_date,
        qty: it.qty, unit: it.unit,
        notes: "",
      }));
      if (qcLots.length > 0) {
        setQcQueue({ lots: qcLots, index: 0 });
      } else {
        alert(r.fully_received ? "Tüm kalemler teslim alındı (adet birimli, QC gerekmez)." : "Kısmi teslim kaydedildi.");
      }
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const addLine = () => setF({ ...f, lines: [...f.lines, { material_id: "", qty: 0, unit: "kg", unit_price: 0 }] });
  const upd = (i, k, v) => {
    const l = [...f.lines];
    l[i] = { ...l[i], [k]: v };
    // If material changed, auto-pick unit from material master
    if (k === "material_id" && v) {
      const m = materials.find(mm => mm.id === v);
      if (m && m.unit) l[i].unit = m.unit;
    }
    setF({ ...f, lines: l });
  };
  const mname = id => materials.find(m => m.id === id)?.name || "—";

  // [iter96] Fix Pkg 02 C6/C7/C8/C9 — Yeni Envanter Ekle akışı.
  // PO create formu state'i KORUNUR (setF çağrılmaz), sadece yeni material create edilir
  // ve başarılı response'ta PO line'ının material_id'si otomatik doldurulur.
  const openNewMaterial = (lineIdx, prefillName) => {
    setNewMatModal({
      lineIdx,
      prefillName,
      form: {
        name: prefillName,
        category: "raw_material",
        unit: f.lines[lineIdx]?.unit || "kg",
        unit_cost: 0, currency: "EUR",
        critical_stock: 0, min_stock: 0, lead_time_days: 7,
        primary_supplier: f.supplier || "",
      },
    });
  };
  const saveNewMaterial = async () => {
    if (newMatBusy || !newMatModal) return;
    const nf = newMatModal.form;
    if (!nf.name?.trim()) { alert("Malzeme adı zorunlu"); return; }
    setNewMatBusy(true);
    let matId = null;
    try {
      const mat = await api.post("/materials", {
        name: nf.name.trim(),
        category: nf.category, unit: nf.unit || "kg",
        unit_cost: parseFloat(nf.unit_cost || 0), currency: nf.currency || "EUR",
        min_stock: parseFloat(nf.min_stock || 0),
        critical_stock: parseFloat(nf.critical_stock || 0),
        lead_time_days: parseInt(nf.lead_time_days || 0),
        primary_supplier: nf.primary_supplier || "",
      });
      matId = mat.id;
    } catch (e) {
      // Idempotency (C9): duplicate → mevcut material'ı kullan.
      const err = e.response?.data;
      if (err?.detail?.code === "duplicate_material_name" && err.detail.existing_id) {
        matId = err.detail.existing_id;
      } else {
        alert(err?.detail?.message || err?.detail || "Envanter kaydı oluşturulamadı");
        setNewMatBusy(false);
        return;
      }
    }
    // Materials listesini refresh et ve PO line'ını otomatik seç.
    try {
      const fresh = await api.get("/materials");
      setMaterials(Array.isArray(fresh) ? fresh : []);
    } catch (_) {}
    const idx = newMatModal.lineIdx;
    setF(prev => {
      const l = [...prev.lines];
      if (l[idx]) {
        l[idx] = { ...l[idx], material_id: matId };
        // Auto-pick unit from freshly created material
        if (nf.unit) l[idx].unit = nf.unit;
      }
      return { ...prev, lines: l };
    });
    setNewMatBusy(false);
    setNewMatModal(null);
  };
  return (
    <div>
      <PageHeader title="Satın Alma" description="İhtiyaç listeleri, satın alma siparişleri ve teslim alma takibi" actions={<Btn onClick={() => { setEditing(null); setF(empty); setOpen(true); }} data-testid="new-po"><Plus size={14}/> Yeni PO</Btn>}/>
      <MeetingsBanner department="PURCHASING" />
      <div className="border-b mb-4 flex gap-4">
        {[
          { k: "pos", l: `Siparişler (${pos.length})` },
          { k: "needs", l: `İhtiyaç Listesi (${needs.length})` },
          { k: "critical", l: `Kritik Stok (${critical.length})` },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`purch-tab-${t.k}`}
            className={`px-1 py-2 text-sm border-b-2 ${tab === t.k ? "border-indigo-600 text-indigo-700 font-medium" : "border-transparent text-slate-500"}`}>{t.l}</button>
        ))}
      </div>
      {/* iter272 — İhtiyaç Listesi (SO-driven net needs) */}
      {tab === "needs" && (() => {
        // iter273 — Toplu sipariş desteği (İhtiyaç Listesi)
        // Aynı tedarikçili seçili malzemeleri tek PO'ya birleştir.
        const selectedRows = needsFiltered.filter(r => needsSelected.has(r.material_id));
        const supplierGroup = (() => {
          if (selectedRows.length === 0) return null;
          const supKey = selectedRows[0].preferred_supplier_name || "__none__";
          const allSame = selectedRows.every(r => (r.preferred_supplier_name || "__none__") === supKey);
          return { allSame, supplier: selectedRows[0].preferred_supplier_name, count: selectedRows.length };
        })();
        const PRIO_RANK = { urgent: 3, normal: 2, low: 1 };
        const bulkCreatePo = () => {
          if (!supplierGroup?.allSame) return;
          const maxPrio = selectedRows.reduce((mx, r) => {
            const rk = PRIO_RANK[r.priority] || 2;
            return rk > (PRIO_RANK[mx] || 2) ? r.priority : mx;
          }, "low");
          setEditing(null);
          setF({
            supplier: supplierGroup.supplier || "",
            expected_delivery: "",
            priority: maxPrio,
            linked_so_id: null, linked_so_no: null,
            lines: selectedRows.map(r => ({
              material_id: r.material_id, qty: r.net_shortage,
              unit: r.unit || "kg", unit_price: 0,
            })),
          });
          setNeedsSelected(new Set());
          setOpen(true);
        };
        const toggleRow = (mid) => {
          setNeedsSelected(prev => {
            const nx = new Set(prev);
            if (nx.has(mid)) nx.delete(mid); else nx.add(mid);
            return nx;
          });
        };
        const toggleAll = () => {
          setNeedsSelected(prev => {
            if (prev.size === needsFiltered.length && needsFiltered.length > 0) return new Set();
            return new Set(needsFiltered.map(r => r.material_id));
          });
        };
        const allChecked = needsFiltered.length > 0 && needsSelected.size === needsFiltered.length;
        return (<>
        <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-3 text-xs text-indigo-900" data-testid="needs-info-banner">
          <b>ℹ İhtiyaç Listesi:</b> Bu liste satış siparişlerinden doğan ve henüz karşılanmamış <b>net malzeme ihtiyacını</b> gösterir. Mevcut MRP hesabı üzerinden alınır (rezervasyon phantom kayıtları) ve açık satın alma siparişleri düşülür. Öncelik ilgili satış siparişinden gelir; birden fazla SO varsa <b>en yüksek öncelik</b> gösterilir. Aynı tedarikçili birden fazla malzemeyi seçip <b>tek PO</b>'da birleştirebilirsiniz.
        </div>
        <div className="flex gap-2 items-center mb-2 flex-wrap">
          <input type="text" placeholder="Malzeme / SO / müşteri ara..." value={needsFilter.q}
            onChange={e => setNeedsFilter({ ...needsFilter, q: e.target.value })}
            className="px-2 py-1 text-sm border rounded w-72" data-testid="needs-filter-q"/>
          <select value={needsFilter.priority} onChange={e => setNeedsFilter({ ...needsFilter, priority: e.target.value })}
            className="px-2 py-1 text-sm border rounded" data-testid="needs-filter-priority">
            <option value="">Tüm Öncelikler</option>
            <option value="urgent">Acil</option>
            <option value="normal">Normal</option>
            <option value="low">Düşük</option>
          </select>
          {selectedRows.length > 0 && (
            <div className="flex items-center gap-2 ml-2 px-2 py-1 rounded border border-indigo-300 bg-indigo-50" data-testid="needs-bulk-bar">
              <span className="text-xs font-semibold text-indigo-800">{selectedRows.length} seçildi</span>
              {supplierGroup?.allSame ? (
                <Btn size="sm" onClick={bulkCreatePo} data-testid="needs-bulk-po-btn">
                  <ShoppingCart size={12}/> Aynı Tedarikçi ({supplierGroup.supplier || "atanmamış"}) — Tek PO'da Birleştir
                </Btn>
              ) : (
                <span className="text-[11px] text-red-600" data-testid="needs-bulk-supplier-warn">
                  ⚠ Seçili malzemeler farklı tedarikçilere ait — birleştirme için aynı tedarikçili malzeme seçin
                </span>
              )}
              <button type="button" onClick={() => setNeedsSelected(new Set())} className="text-[11px] text-slate-500 underline ml-1" data-testid="needs-bulk-clear">Temizle</button>
            </div>
          )}
          <span className="text-[11px] text-slate-500 ml-auto">{needsFiltered.length} / {needs.length} kayıt</span>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="needs-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-2 py-2 w-6"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="needs-select-all" aria-label="Tümünü seç"/></th>
              {["Kod","Malzeme","Birim","İlgili SO'lar","Öncelik","Toplam İhtiyaç","Karşılanabilen Stok","Açık PO","Net Eksik","Tedarikçi","İşlem"].map((h,i) =>
                <th key={i} className="text-left px-2 py-2 font-semibold text-slate-600 uppercase text-[10px]">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {pgNeeds.paged.length === 0 && <tr><td colSpan={12} className="text-center py-10 text-emerald-600">✓ Satış siparişlerinden doğan net malzeme ihtiyacı yok.</td></tr>}
            {pgNeeds.paged.map(r => (
              <tr key={r.material_id} className={`border-b border-slate-100 hover:bg-slate-50 ${needsSelected.has(r.material_id) ? "bg-indigo-50/60" : ""}`} data-testid={`needs-row-${r.material_code || r.material_id}`}>
                <td className="px-2 py-1.5"><input type="checkbox" checked={needsSelected.has(r.material_id)} onChange={() => toggleRow(r.material_id)} data-testid={`needs-select-${r.material_code || r.material_id}`}/></td>
                <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{r.material_code || "—"}</td>
                <td className="px-2 py-1.5 font-medium text-slate-800">{r.material_name}</td>
                <td className="px-2 py-1.5 text-slate-600">{r.unit || "—"}</td>
                <td className="px-2 py-1.5">
                  <div className="flex flex-wrap gap-1">
                    {(r.sales_orders || []).map((s, i) => (
                      <Link key={i} to={`/sales-orders`} title={`${s.customer_name} · ${s.qty} ${r.unit}`}
                        className="text-[10px] bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 hover:bg-indigo-100 text-indigo-800 font-mono">
                        {s.so_no} · {s.qty} {r.unit}
                      </Link>
                    ))}
                  </div>
                </td>
                <td className="px-2 py-1.5">{prioBadge(r.priority)}</td>
                <td className="px-2 py-1.5 font-mono text-slate-700">{r.total_need} {r.unit}</td>
                <td className="px-2 py-1.5 font-mono text-slate-500">{r.on_hand_available} {r.unit}</td>
                <td className="px-2 py-1.5 font-mono text-blue-600">{r.open_po_qty} {r.unit}</td>
                <td className="px-2 py-1.5 font-mono font-bold text-red-600">{r.net_shortage} {r.unit}</td>
                <td className="px-2 py-1.5 text-[11px] text-slate-600">{r.preferred_supplier_name || <span className="text-slate-300 italic">—</span>}</td>
                <td className="px-2 py-1.5">
                  <Btn size="sm" onClick={() => quickCreateFromNeed(r)} data-testid={`needs-quick-po-${r.material_code || r.material_id}`}>
                    <ShoppingCart size={12}/> Hızlı Sipariş
                  </Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <ListPagination pagination={pgNeeds} testid="purch-needs-pagination"/>
        </>);
      })()}
      {/* iter272 — Kritik Stok Listesi (STRICT 0 < avail < critical) */}
      {tab === "critical" && (
        <>
        <div className="mb-3 bg-orange-50 border border-orange-200 rounded p-3 text-xs text-orange-900" data-testid="critical-info-banner">
          <b>ℹ Kritik Stok Listesi:</b> Yalnızca <b>0'dan büyük fakat kritik seviyenin altında</b> stoğu olan malzemeler. Stoğu sıfır olan malzemeler burada gösterilmez (İhtiyaç Listesi'ne bakınız). Eşitlik durumunda listelenmez.
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="critical-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Kod","Malzeme","Mevcut Stok","Kritik Stok","Kritik Seviyeye Eksik","Açık PO","Birim","İşlem"].map((h,i) =>
                <th key={i} className="text-left px-2 py-2 font-semibold text-slate-600 uppercase text-[10px]">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {pgCritical.paged.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-emerald-600">✓ Kritik seviyenin altında (pozitif stok) malzeme yok.</td></tr>}
            {pgCritical.paged.map(r => (
              <tr key={r.material_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`critical-row-${r.material_code || r.material_id}`}>
                <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{r.material_code || "—"}</td>
                <td className="px-2 py-1.5 font-medium text-slate-800">{r.material_name}</td>
                <td className="px-2 py-1.5 font-mono text-slate-700">{r.on_hand}</td>
                <td className="px-2 py-1.5 font-mono text-orange-700">{r.critical_stock}</td>
                <td className="px-2 py-1.5 font-mono font-bold text-red-600">{r.shortage_to_critical}</td>
                <td className="px-2 py-1.5 font-mono text-blue-600">{r.open_po_qty}</td>
                <td className="px-2 py-1.5 text-slate-600">{r.unit || "—"}</td>
                <td className="px-2 py-1.5">
                  {(() => {
                    // Zaten açık PO ile kritik farkı karşılanmışsa öneri gösterme (spec §7)
                    const covered = r.open_po_qty >= r.shortage_to_critical - 0.0001;
                    if (covered) return <span className="text-[10px] text-slate-400 italic">Açık PO yeterli</span>;
                    return (
                      <Btn size="sm" onClick={() => quickCreateFromCritical(r)} data-testid={`critical-quick-po-${r.material_code || r.material_id}`}>
                        <ShoppingCart size={12}/> Hızlı Sipariş
                      </Btn>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <ListPagination pagination={pgCritical} testid="purch-critical-pagination"/>
        </>
      )}
      {tab === "reqs" && (
        <>
        <div className="mb-3 bg-orange-50 border border-orange-200 rounded p-3 text-xs text-orange-900">
          <b>ℹ Kritik Stok İhtiyaç Listesi:</b> Bu liste artık satış siparişlerine göre değil, doğrudan envanterde <b>kritik seviyeye</b> ({"available ≤ critical_stock"}) düşmüş her malzemeyi gösterir. Malzemenin ana kayıtındaki <code>critical_stock</code> ve <code>min_stock</code> alanları belirleyicidir. Açık satın alma siparişleri (yolda) net eksikten düşülür.
        </div>
        <Table columns={[
          { label: "Malzeme", render: r => `${r.material_name} (${r.material_code || ""})` },
          { label: "Durum", render: r => <Badge level={r.level === "missing" ? "missing" : r.level === "critical" ? "critical" : "attention"}>{r.level === "missing" ? "Stokta Yok" : r.level === "critical" ? "Kritik" : "Az"}</Badge> },
          { label: "Eldeki", render: r => <span className={`font-mono ${r.on_hand <= 0 ? "text-red-600 font-bold" : ""}`}>{r.on_hand} {r.unit || ""}</span> },
          { label: "Kritik Seviye", render: r => <span className="font-mono text-orange-700">{r.critical_stock || "—"} {r.unit || ""}</span> },
          { label: "Min. Seviye", render: r => <span className="font-mono text-slate-500">{r.min_stock || "—"} {r.unit || ""}</span> },
          { label: "Hedef Seviye", render: r => <span className="font-mono">{r.target_level} {r.unit || ""}</span> },
          { label: "Sipariş Verilmiş (Yolda)", render: r => <span className="text-blue-600 font-mono">{r.ordered_qty} {r.unit || ""}</span> },
          { label: "Net Eksik", render: r => r.net_shortage > 0
              ? <span className="text-red-600 font-mono font-bold">{r.net_shortage} {r.unit || ""}</span>
              : <span className="text-emerald-600 font-mono">✓ 0</span>
          },
          { label: "Tercih Edilen Tedarikçi", render: r => r.preferred_supplier_name || <span className="text-slate-400 italic text-xs">Atanmamış</span> },
          { label: "Tedarik Süresi", render: r => `${r.lead_time_days} gün` },
          { label: "İşlem", render: r => r.net_shortage > 0 ? (
              <Btn size="sm" onClick={() => {
                setEditing(null);
                setF({
                  supplier: r.preferred_supplier_name || "",
                  expected_delivery: "",
                  lines: [{ material_id: r.material_id, qty: r.net_shortage, unit: r.unit || "kg", unit_price: 0 }],
                });
                setTab("pos");
                setOpen(true);
              }} data-testid={`req-po-${r.material_code || r.material_id}`}><ShoppingCart size={12}/> Sipariş Oluştur</Btn>
            ) : <span className="text-slate-300 text-xs">—</span> },
        ]} rows={pgReqs.paged} />
        <ListPagination pagination={pgReqs} testid="purch-reqs-pagination"/>
        {reqs.length === 0 && <div className="text-center py-8 text-emerald-600 text-sm">✓ Envanterde kritik seviyenin altında malzeme yok.</div>}
        </>
      )}
      {tab === "pos" && (
        <>
        <FilterAndReportBar
          reportType="purchasing" rows={pos}
          statuses={[{value:"draft",label:"Taslak"},{value:"ordered",label:"Sipariş Verildi"},{value:"partial_received",label:"Kısmi Teslim"},{value:"received",label:"Teslim Alındı"},{value:"cancelled",label:"İptal"}]}
          dateAccessor={r => r.created_at || r.order_date}
          supplierAccessor={r => r.supplier}
          productAccessor={r => (r.lines||[]).map(ln => materials.find(m=>m.id===ln.material_id)?.name || "").join(" ")}
          statusAccessor={r => r.status}
          onFiltered={setFilteredPos}
          showSupplier={true}
        />
        <Table columns={[
          { label: "Sipariş Numarası", render: r => <span className="font-mono text-xs">{r.po_no}</span> },
          { label: "Tedarikçi", key: "supplier" },
          { label: "Sipariş Tarihi", render: r => (r.order_date || r.created_at || "").slice(0, 10) },
          { label: "Teslim Tarihi", render: r => (r.expected_delivery || "").slice(0, 10) },
          { label: "Durum", render: r => <Badge level={r.status === "received" ? "ok" : r.status === "partial_received" ? "attention" : r.status === "cancelled" ? "inactive" : "attention"}>{r.status}</Badge> },
          { label: "İşlem", render: r => {
            const done = isFullyReceived(r);
            return (
              <div className="flex gap-1 flex-wrap">
                {r.status === "draft" && <Btn size="sm" onClick={() => approve(r.id)} data-testid={`approve-po-${r.id}`}>Onayla</Btn>}
                {(r.status === "ordered" || r.status === "partial_received") && !done && <Btn size="sm" onClick={() => openReceive(r)} data-testid={`receive-po-${r.id}`}>Teslim Al</Btn>}
                {r.status === "partial_received" && canCompletePO && (
                  <Btn size="sm" variant="secondary" onClick={() => completePO(r)} data-testid={`complete-po-${r.id}`} title="Eksik kalan miktar stoğa eklenmez; PO manuel olarak Tamamlandı durumuna geçer">
                    ✓ Teslimatı Tamamla
                  </Btn>
                )}
                {done && <Badge level="ok">Tümü Teslim Alındı</Badge>}
                {/* [iter72] Etiket Yazdır — sadece TESLİM ALINMIŞ (partial veya tam) PO'lar için (msg-93 madde 10, 44) */}
                {(r.status === "received" || r.status === "partial_received") && (
                  <Btn size="sm" variant="secondary" onClick={() => openLabelPicker(r)} data-testid={`label-po-${r.id}`}><Tag size={12}/> Etiket Yazdır</Btn>
                )}
                {(r.status === "draft" || r.status === "ordered") && <Btn size="sm" variant="secondary" onClick={() => cancel(r.id, r.po_no)} data-testid={`cancel-po-${r.id}`}>İptal Et</Btn>}
                {r.status !== "completed" && r.status !== "cancelled" && <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`edit-po-${r.id}`}>Düzenle</Btn>}
                {r.status !== "completed" && <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`delete-po-${r.id}`}>Sil</Btn>}
              </div>
            );
          } },
          { label: "Gönderim", render: r => (
            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
              <PoTelegramBtn po={r} suppliers={suppliers} onSent={load}/>
              <PoEmailBtn po={r} suppliers={suppliers} onSent={load}/>
            </div>
          )},
          { label: "Takip", render: r => (r.deliveries?.length > 0)
            ? <Btn size="sm" variant="secondary" onClick={() => setHistoryOpen(r)} data-testid={`hist-po-${r.id}`}>Geçmiş ({r.deliveries.length})</Btn>
            : <span className="text-[10px] text-slate-400 italic">—</span>
          },
        ]} rows={pgPos.paged} />
        <ListPagination pagination={pgPos} testid="purch-pos-pagination"/>
        </>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "PO Düzenle" : "Yeni Satın Alma Siparişi"} size="lg">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Tedarikçi *" value={f.supplier} onChange={e => setF({ ...f, supplier: e.target.value })} data-testid="po-supplier">
            <option value="">Seçiniz...</option>
            {suppliers.filter(s => s.active).map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </Select>
          <Input label="Beklenen Teslim" type="date" value={(f.expected_delivery || "").slice(0,10)} onChange={e => setF({ ...f, expected_delivery: e.target.value })} />
        </div>
        <div className="text-[10px] text-slate-500 italic mt-1">Not: Birim fiyat Envanter modülünde malzeme başına düzenlenir; PO'da fiyat sorulmaz.</div>
        <div className="mt-3">
          {f.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 bg-white border rounded p-2">
              <div className="col-span-7">
                {/* iter96 Fix Pkg 02 Bölüm C1-C7: editable autocomplete + Yeni Envanter Ekle fallback */}
                <MaterialCombobox
                  value={l.material_id}
                  materials={materials}
                  onChange={(mid) => upd(i, "material_id", mid)}
                  onCreate={(typedName) => openNewMaterial(i, typedName)}
                  testid={`po-line-mat-${i}`}
                  placeholder="Malzeme adı yazın veya seçin..."
                />
              </div>
              <input type="number" step="0.01" value={l.qty} onChange={e => upd(i, "qty", parseFloat(e.target.value))} placeholder="Miktar" className="col-span-3 px-2 py-1 text-xs border rounded" data-testid={`po-line-qty-${i}`}/>
              <select value={l.unit || "kg"} onChange={e => upd(i, "unit", e.target.value)} className="col-span-2 px-2 py-1 text-xs border rounded" data-testid={`po-line-unit-${i}`}>
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="l">l</option>
                <option value="ml">ml</option>
                <option value="adet">adet</option>
                <option value="box">kutu</option>
                <option value="carton">koli</option>
                <option value="roll">rulo</option>
              </select>
            </div>
          ))}
          <Btn size="sm" variant="secondary" onClick={addLine}><Plus size={12}/> Ekle</Btn>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-po">Kaydet</Btn></div>
      </Modal>

      {/* iter96 Fix Pkg 02 Bölüm C6/C7: Yeni Envanter Ekle inline modal. PO formu state'i korunur. */}
      <Modal open={!!newMatModal} onClose={() => !newMatBusy && setNewMatModal(null)} title={`Yeni Envanter Kaydı — ${newMatModal?.prefillName || ""}`} size="lg">
        <div className="text-xs text-slate-600 mb-3 bg-indigo-50 border border-indigo-200 rounded p-2">
          Bu envanter kaydı oluşturulduktan sonra Satın Alma formuna otomatik geri dönülecek ve yeni malzeme seçili olacak. Tedarikçi, tarih ve diğer alanlarınız kaybolmayacak.
        </div>
        {newMatModal && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Input label="Malzeme Adı *" value={newMatModal.form.name} onChange={e => setNewMatModal({ ...newMatModal, form: { ...newMatModal.form, name: e.target.value } })} data-testid="new-mat-name"/></div>
            <Select label="Kategori *" value={newMatModal.form.category} onChange={e => setNewMatModal({ ...newMatModal, form: { ...newMatModal.form, category: e.target.value } })} data-testid="new-mat-category">
              <option value="raw_material">Ham Madde</option>
              <option value="packaging">Ambalaj</option>
              <option value="other_packaging">Diğer Ambalaj</option>
              <option value="label">Etiket</option>
              <option value="consumable">Sarf</option>
            </Select>
            <Select label="Birim *" value={newMatModal.form.unit} onChange={e => setNewMatModal({ ...newMatModal, form: { ...newMatModal.form, unit: e.target.value } })} data-testid="new-mat-unit">
              <option value="kg">kg</option>
              <option value="g">g</option>
              <option value="l">l</option>
              <option value="ml">ml</option>
              <option value="adet">adet</option>
              <option value="box">kutu</option>
              <option value="carton">koli</option>
              <option value="roll">rulo</option>
            </Select>
            <Input label="Birim Maliyet" type="number" step="0.0001" value={newMatModal.form.unit_cost} onChange={e => setNewMatModal({ ...newMatModal, form: { ...newMatModal.form, unit_cost: e.target.value } })} data-testid="new-mat-cost"/>
            <Select label="Para Birimi" value={newMatModal.form.currency} onChange={e => setNewMatModal({ ...newMatModal, form: { ...newMatModal.form, currency: e.target.value } })} data-testid="new-mat-currency">
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="TRY">TRY</option>
            </Select>
            <Input label="Kritik Stok" type="number" step="0.01" value={newMatModal.form.critical_stock} onChange={e => setNewMatModal({ ...newMatModal, form: { ...newMatModal.form, critical_stock: e.target.value } })} data-testid="new-mat-critical"/>
            <Input label="Tedarik Süresi (gün)" type="number" step="1" value={newMatModal.form.lead_time_days} onChange={e => setNewMatModal({ ...newMatModal, form: { ...newMatModal.form, lead_time_days: e.target.value } })} data-testid="new-mat-lead"/>
            <div className="col-span-2"><Input label="Ana Tedarikçi (opsiyonel)" value={newMatModal.form.primary_supplier} onChange={e => setNewMatModal({ ...newMatModal, form: { ...newMatModal.form, primary_supplier: e.target.value } })} data-testid="new-mat-supplier"/></div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setNewMatModal(null)} disabled={newMatBusy}>Vazgeç</Btn>
          <Btn onClick={saveNewMaterial} disabled={newMatBusy} data-testid="save-new-mat">
            {newMatBusy ? <><Loader2 size={12} className="animate-spin"/> Kaydediliyor…</> : "Kaydet & Bağla"}
          </Btn>
        </div>
      </Modal>

      <Modal open={!!receiveOpen} onClose={() => setReceiveOpen(null)} title={`Teslim Alma — ${receiveOpen?.po_no || ""} (${receiveOpen?.supplier || ""})`} size="full">
        <div className="text-xs text-slate-600 mb-3 bg-amber-50 border border-amber-200 rounded p-2">
          Kalemleri seçin, teslim alınan miktarı girin. <b>kg / g / l / ml birimli</b> kalemlerde Lot No + Üretim Tarihi + SKT zorunludur. <b>Adet / kutu / koli</b> gibi ambalaj kalemlerinde bu alanlar boş bırakılabilir.
          <br/><span className="text-emerald-800 font-medium">Fazla teslim alabilirsiniz</span> — sipariş miktarı tarihsel kayıt olarak korunur; envantere gerçek teslim miktarı eklenir.
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Input label="Teslim Alan Kişi *" value={rec.received_by_name} onChange={e => setRec({ ...rec, received_by_name: e.target.value })} data-testid="receive-name" placeholder="Ad Soyad"/>
          <Input label="Teslim Tarihi" type="date" value={rec.received_at} onChange={e => setRec({ ...rec, received_at: e.target.value })} data-testid="receive-date"/>
          <div className="col-span-2"><Textarea label="Not" value={rec.notes} onChange={e => setRec({ ...rec, notes: e.target.value })} data-testid="receive-notes" placeholder="Örn. paket sağlam geldi..."/></div>
        </div>
        <div className="overflow-x-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-100">
              <tr>
                {["", "Malzeme", "Kalan", "Miktar", "Lot No", "Üretim Tarihi", "SKT", "Etiket Tara"].map(h => <th key={h} className="px-2 py-1.5 text-left text-[11px] font-semibold text-slate-600 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rec.lines.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-400">Kalan teslim alınacak kalem yok.</td></tr>}
              {rec.lines.map((l, i) => {
                const isKg = ["kg","g","l","ml"].includes((l.unit||"kg").toLowerCase());
                const confCls = (k) => {
                  const c = l.confidence?.[k];
                  return c === "low" ? "border-rose-500 bg-rose-50" : (c === "none" ? "border-rose-500 bg-rose-50" : "");
                };
                const confIcon = (k) => {
                  const c = l.confidence?.[k];
                  if (c === "low") return <span className="text-[10px] text-rose-600 ml-1">⚠ Kontrol Edin</span>;
                  if (c === "none") return <span className="text-[10px] text-rose-600 ml-1">Okunamadı</span>;
                  return null;
                };
                return (
                <tr key={l.idx} className={`border-t ${l.selected ? "bg-emerald-50/40" : ""}`}>
                  <td className="px-2 py-1.5"><input type="checkbox" checked={l.selected} onChange={e => updLine(i, "selected", e.target.checked)} data-testid={`rcv-cb-${i}`}/></td>
                  <td className="px-2 py-1.5">{mname(l.material_id)} {!isKg && <span className="text-[10px] text-slate-400">(adet — lot gerekmez)</span>}</td>
                  <td className="px-2 py-1.5 font-mono">{l.remaining} {l.unit}</td>
                  <td className="px-2 py-1.5"><input type="number" step="0.01" value={l.qty} onChange={e => updLine(i, "qty", e.target.value)} disabled={!l.selected} className="w-20 px-1 py-0.5 border rounded" data-testid={`rcv-qty-${i}`}/></td>
                  <td className="px-2 py-1.5">
                    {isKg ? (
                      <div className="flex items-center gap-1">
                        <input value={l.lot_no || ""} onChange={e => updLine(i, "lot_no", e.target.value)} disabled={!l.selected} placeholder="LOT-2026-001" className={`w-32 px-1 py-0.5 border rounded ${confCls("lot_no")}`} data-testid={`rcv-lot-${i}`}/>
                        {confIcon("lot_no")}
                      </div>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    {isKg ? (
                      <div className="flex items-center gap-1">
                        <TrDateInput value={l.mfg_date} onChange={v => updLine(i, "mfg_date", v)} disabled={!l.selected} className={`w-28 px-1 py-0.5 border rounded font-mono text-xs ${confCls("mfg_date")}`} data-testid={`rcv-mfg-${i}`}/>
                        {confIcon("mfg_date")}
                      </div>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    {isKg ? (
                      <div className="flex items-center gap-1">
                        <TrDateInput value={l.expiry_date} onChange={v => updLine(i, "expiry_date", v)} disabled={!l.selected} className={`w-28 px-1 py-0.5 border rounded font-mono text-xs ${confCls("expiry_date")}`} data-testid={`rcv-exp-${i}`}/>
                        {confIcon("expiry_date")}
                      </div>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    {isKg && (
                      <div className="flex items-center gap-1" data-testid={`rcv-ocr-cell-${i}`}>
                        <button
                          type="button"
                          onClick={() => setCameraOpenIdx(i)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border border-slate-200 bg-white hover:bg-sky-50"
                          title="Kamera ile Çek"
                          data-testid={`rcv-ocr-camera-${i}`}
                        >
                          {(ocrLine?.idx === i && ocrLine?.busy) ? <Loader2 size={12} className="animate-spin"/> : <Camera size={12}/>}
                        </button>
                        <label
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border border-slate-200 bg-white hover:bg-emerald-50 cursor-pointer"
                          title="Galeriden Seç"
                          data-testid={`rcv-ocr-gallery-${i}`}
                        >
                          <ImageIcon size={12}/>
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) runOcr(i, f); e.target.value = ""; }}/>
                        </label>
                        {(l.label_photo_ids || []).length > 0 && (
                          <LabelPhotoStrip photoIds={l.label_photo_ids} size={32} testid={`rcv-ocr-strip-${i}`}/>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setReceiveOpen(null)}>Vazgeç</Btn><Btn onClick={doReceive} data-testid="do-receive">Teslimatı Onayla</Btn></div>
      </Modal>
      <CameraCapture
        open={cameraOpenIdx !== null}
        onClose={() => setCameraOpenIdx(null)}
        onCapture={(file) => { const i = cameraOpenIdx; setCameraOpenIdx(null); if (i !== null) runOcr(i, file); }}
        title="Tedarikçi Etiketi — Fotoğraf Çek"
      />

      <Modal open={!!qcQueue} onClose={() => {}} title={qcQueue ? `Hammadde QC — ${qcQueue.lots[qcQueue.index]?.lot_code} (${qcQueue.index + 1}/${qcQueue.lots.length})` : ""} size="lg">
        {qcQueue && (() => {
          const cur = qcQueue.lots[qcQueue.index];
          const setNotes = v => { const q = { ...qcQueue }; q.lots[q.index].notes = v; setQcQueue(q); };
          const advance = () => {
            const next = qcQueue.index + 1;
            if (next >= qcQueue.lots.length) { setQcQueue(null); alert("✓ Hammadde QC turu tamamlandı."); }
            else setQcQueue({ ...qcQueue, index: next });
          };
          const approve = async () => {
            try {
              await api.post(`/lots/${cur.lot_id}/approve`, { notes: cur.notes });
              advance();
            } catch (e) { alert(e.response?.data?.detail || "QC onayı verilemedi"); }
          };
          const quarantine = async () => {
            try {
              await api.post(`/lots/${cur.lot_id}/quarantine`, { reason: cur.notes || "Uygun değil" });
              advance();
            } catch (e) { alert(e.response?.data?.detail || "Karantinaya alınamadı"); }
          };
          return (
            <div>
              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-amber-900">
                Bu lot QC değerlendirmesi bekliyor. "Uygun" seçilirse ana stoğa girer, "Uygun Değil" seçilirse karantinaya alınır (not eklemek opsiyoneldir).
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 border rounded p-3 mb-3">
                <div><span className="text-slate-500">Malzeme:</span> <b>{cur.material_name}</b></div>
                <div><span className="text-slate-500">Lot Kodu:</span> <span className="font-mono">{cur.lot_code}</span></div>
                <div><span className="text-slate-500">Tedarikçi Lot:</span> {cur.supplier_lot || "—"}</div>
                <div><span className="text-slate-500">Miktar:</span> <span className="font-mono">{cur.qty} {cur.unit}</span></div>
                <div><span className="text-slate-500">Üretim:</span> {cur.mfg_date || "—"}</div>
                <div><span className="text-slate-500">SKT:</span> {cur.expiry_date || "—"}</div>
              </div>
              <div className="mt-3"><Textarea label="Not (opsiyonel — ret sebebi veya gözlem)" value={cur.notes || ""} onChange={e => setNotes(e.target.value)} data-testid="qc-simple-note" /></div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <Btn variant="danger" onClick={quarantine} data-testid="qc-simple-reject" size="xl">⛔ Uygun Değil (Karantinaya Al)</Btn>
                <Btn variant="approve" onClick={approve} data-testid="qc-simple-approve" size="xl"><ShieldCheck size={16}/> ✓ Uygun (Ana Stoğa Al)</Btn>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal open={!!historyOpen} onClose={() => setHistoryOpen(null)} title={`Teslimat Geçmişi — ${historyOpen?.po_no || ""}`} size="lg">
        <div className="space-y-3">
          {(historyOpen?.deliveries || []).length === 0 && <div className="text-sm text-slate-400 text-center py-6">Henüz teslimat yok</div>}
          {(historyOpen?.deliveries || []).map((d, i) => (
            <div key={d.id || i} className="border-l-2 border-emerald-500 bg-emerald-50/40 pl-3 py-2 rounded">
              <div className="flex justify-between text-sm">
                <span className="font-semibold">{d.received_by_name}</span>
                <span className="font-mono text-xs text-slate-500">{(d.received_at || "").slice(0, 19).replace("T", " ")}</span>
              </div>
              {d.notes && <div className="text-xs text-slate-600 mt-1 italic">"{d.notes}"</div>}
              <div className="mt-2 text-xs">
                <div className="text-slate-500 uppercase font-semibold mb-1 text-[10px]">Teslim Alınan Kalemler:</div>
                <table className="w-full">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="text-left font-normal">Malzeme</th>
                      <th className="text-left font-normal">Lot Kodu</th>
                      <th className="text-left font-normal">Ted. Lot</th>
                      <th className="text-left font-normal">SKT</th>
                      <th className="text-right font-normal">Miktar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.items || []).map((it, j) => (
                      <tr key={j} className="border-t border-emerald-200/40">
                        <td className="py-0.5">{it.material_name} <span className="text-slate-400 font-mono">({it.material_code || "—"})</span></td>
                        <td className="py-0.5 font-mono">{it.lot_code || "—"}</td>
                        <td className="py-0.5 font-mono">{it.lot_no || "—"}</td>
                        <td className="py-0.5 font-mono">{(it.expiry_date || "").slice(0, 10) || "—"}</td>
                        <td className="text-right py-0.5 font-mono">{it.qty} {it.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* [iter72] Etiket Yazdır — malzeme + (KG ise) lot seçim modalı (msg-93 Bölüm A) */}
      <Modal open={!!labelPickPo} onClose={() => setLabelPickPo(null)} title={`Etiket Yazdır — ${labelPickPo?.po?.po_no || ""}`} size="lg">
        <div className="text-[11px] text-slate-500 mb-3">
          Aşağıdan malzeme seçin. KG hammadde için lot seçimi zorunludur. Etiket adedi yazıcı üzerinden ayarlanır — burada miktar sorulmaz.
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {(labelPickPo?.lines || []).map((ln, i) => (
            <div key={i} className="border rounded p-2 bg-slate-50/40" data-testid={`label-line-${i}`}>
              <div className="flex justify-between items-start mb-1">
                <div>
                  <div className="text-sm font-semibold">{ln.material_name || ln.material_id}</div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {ln.material_code || "—"} · Birim: {ln.unit || "—"} · Teslim Alınan: {ln.received_qty} {ln.unit}
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded ${ln.is_kg ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                  {ln.is_kg ? "KG etiket" : "ADET etiket"}
                </span>
              </div>
              {ln.is_kg ? (
                <div className="mt-2">
                  {ln.lots.length === 0 ? (
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] text-amber-700 italic flex-1">Lot kaydı yok — etiketi yine de "Belirlenemedi" alanlarla basabilirsiniz.</div>
                      <Btn size="sm" variant="secondary"
                           onClick={() => pickLabel(ln, null)}
                           data-testid={`label-line-${i}-nolot`}>
                        <Tag size={11}/> Yine de Yazdır
                      </Btn>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {ln.lots.map(lot => (
                        <Btn key={lot.id} size="sm" variant="secondary"
                             onClick={() => pickLabel(ln, lot.id)}
                             data-testid={`label-line-${i}-lot-${lot.id}`}>
                          <Tag size={11}/> {lot.lot_code} {lot.expiry_date ? `· SKT: ${(lot.expiry_date||"").slice(0,10)}` : ""}
                        </Btn>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-2">
                  <Btn size="sm" onClick={() => pickLabel(ln, null)} data-testid={`label-line-${i}-open`}>
                    <Tag size={11}/> Etiketi Aç
                  </Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      </Modal>

      <BoxLabelPreview
        open={!!labelPreview}
        onClose={() => setLabelPreview(null)}
        source="material"
        materialId={labelPreview?.materialId}
        lotId={labelPreview?.lotId}
        poId={labelPreview?.poId}
        labelKind={labelPreview?.labelKind}
      />
    </div>
  );
}

export function ReportsPage() {
  const reports = [
    { key: "inventory", title: "Envanter Raporu", desc: "Tüm malzemelerin güncel stok durumu" },
    { key: "sales-orders", title: "Satış Siparişleri", desc: "Sipariş durumu ve teslimat takibi" },
    { key: "production", title: "Üretim Raporu", desc: "Planlanan vs gerçekleşen üretim, verim" },
    { key: "shipments", title: "Sevkiyat Raporu", desc: "Sevkiyat ve teslimat kayıtları" },
  ];
  return (
    <div>
      <PageHeader title="Raporlar" description="Excel ve PDF olarak dışa aktarma" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {reports.map(r => (
          <Card key={r.key} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <FileText className="text-indigo-600" size={20} />
            </div>
            <div className="text-base font-semibold mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{r.title}</div>
            <div className="text-sm text-slate-500 mb-4">{r.desc}</div>
            <div className="flex gap-2">
              <Btn size="sm" variant="secondary" onClick={() => api.download(`/reports/${r.key}/excel`, `${r.key}.xlsx`)} data-testid={`export-excel-${r.key}`}><Download size={12}/> Excel</Btn>
              <Btn size="sm" variant="secondary" onClick={() => api.download(`/reports/${r.key}/pdf`, `${r.key}.pdf`)} data-testid={`export-pdf-${r.key}`}><Download size={12}/> PDF</Btn>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function NotificationsPage() {
  const [notes, setNotes] = useState([]);
  useEffect(() => { api.get("/notifications").then(setNotes); }, []);
  return (
    <div>
      <PageHeader title="Bildirimler" description="Kritik stok, SKT, gecikme ve QC uyarıları" />
      <Card className="p-3">
        {notes.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">Aktif bildirim yok</div>}
        {notes.map((n, i) => (
          <div key={i} className="border-l-2 pl-3 py-2 mb-2 bg-white flex items-center gap-3"
            style={{ borderColor: n.severity === "critical" ? "#ea580c" : n.severity === "missing" ? "#dc2626" : "#f59e0b" }}>
            <BellIcon size={14} className="text-slate-400" />
            <div className="flex-1">
              <div className="text-sm text-slate-800">{n.message}</div>
              <div className="text-xs text-slate-400 uppercase tracking-wide mt-0.5">{n.kind}</div>
            </div>
            <Badge level={n.severity === "critical" ? "critical" : n.severity === "missing" ? "missing" : "attention"}>{n.severity}</Badge>
          </div>
        ))}
      </Card>
    </div>
  );
}

export function UsersPage() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { email: "", name: "", role: "sales", password: "", active: true, module_permissions: {} };
  const [f, setF] = useState(empty);
  const load = () => Promise.all([api.get("/users"), api.get("/roles")]).then(([u, r]) => { setRows(u); setRoles(r); });
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing) {
        const payload = { name: f.name, role: f.role, active: f.active, module_permissions: f.module_permissions };
        if (f.password) payload.password = f.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", f);
      }
      setOpen(false); setEditing(null); setF(empty); load();
    } catch (e) { alert(e.response?.data?.detail); }
  };
  const del = async (id) => { if (window.confirm("Kullanıcı silinsin/pasife alınsın mı?")) { try { await api.del(`/users/${id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };
  const openEdit = (r) => { setEditing(r); setF({ ...empty, ...r, module_permissions: r.module_permissions || {} }); setOpen(true); };
  const rl = k => roles.find(r => r.key === k)?.label_tr || k;

  const MODULE_KEYS = [
    { key: "dashboard", label: "Kontrol Paneli" }, { key: "customers", label: "Müşteriler" }, { key: "brands", label: "Markalar" },
    { key: "products", label: "Ürünler" }, { key: "formulas", label: "Formüller" }, { key: "materials", label: "Malzemeler" },
    { key: "inventory", label: "Envanter" }, { key: "warehouses", label: "Depolar" }, { key: "purchasing", label: "Satın Alma" },
    { key: "sales_orders", label: "Satış Siparişleri" }, { key: "quotations", label: "Fiyat Teklifleri" }, { key: "production", label: "Üretim Emirleri" }, { key: "quality", label: "Kalite Kontrol" },
    { key: "finished_products", label: "Bitmiş Ürünler" }, { key: "shipments", label: "Sevkiyatlar" }, { key: "traceability", label: "İzlenebilirlik" },
    { key: "rd", label: "R&D Portalı" }, { key: "finance", label: "Finansal Analiz" }, { key: "reports", label: "Raporlar" },
    { key: "suppliers", label: "Tedarikçiler" }, { key: "quarantine", label: "Karantina" }, { key: "gmp", label: "GMP Portalı" }, { key: "notifications", label: "Bildirimler" }, { key: "requests", label: "Talepler" }, { key: "users", label: "Kullanıcılar" }, { key: "settings", label: "Sistem Ayarları" },
  ];

  return (
    <div>
      <PageHeader title="Kullanıcılar" description="Rol tabanlı kullanıcı yönetimi ve modül izinleri" actions={<Btn onClick={() => { setEditing(null); setF(empty); setOpen(true); }} data-testid="new-user"><Plus size={14}/> Yeni Kullanıcı</Btn>} />
      <Table testid="users-table" columns={[
        { label: "Email", key: "email" },
        { label: "Ad", key: "name" },
        { label: "Rol", render: r => rl(r.role) },
        { label: "Durum", render: r => <Badge level={r.active ? "ok" : "inactive"}>{r.active ? "Aktif" : "Pasif"}</Badge> },
        { label: "İşlem", render: r => (
          <div className="flex gap-1">
            <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`edit-user-${r.id}`}>Düzenle</Btn>
            <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`delete-user-${r.id}`}>Sil</Btn>
          </div>
        ) },
      ]} rows={rows} />
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Kullanıcı Düzenle — ${editing.email}` : "Yeni Kullanıcı"} size="full">
        <div className="grid grid-cols-2 gap-3 mb-4">
          {!editing && <Input label="E-posta *" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="user-email"/>}
          <Input label="Ad Soyad *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="user-name"/>
          <Select label="Rol *" value={f.role} onChange={e => setF({ ...f, role: e.target.value })}>{roles.map(r => <option key={r.key} value={r.key}>{r.label_tr}</option>)}</Select>
          <Input label={editing ? "Yeni Şifre (opsiyonel)" : "Şifre *"} type="password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })} data-testid="user-password"/>
          <Select label="Durum" value={f.active ? "1" : "0"} onChange={e => setF({ ...f, active: e.target.value === "1" })}><option value="1">Aktif</option><option value="0">Pasif</option></Select>
        </div>
        <div className="border rounded-lg p-3 bg-slate-50/50">
          <div className="text-sm font-semibold mb-1">Modül Erişim İzinleri</div>
          <div className="text-xs text-slate-500 mb-3">Bu kullanıcının erişebileceği sayfaları seçin. Admin rolü tüm sayfalara erişir. Hiçbir şey seçmezseniz varsayılan olarak tüm sayfalara erişir.</div>
          <div className="flex flex-wrap gap-2 mb-2">
            <Btn size="sm" variant="secondary" onClick={() => { const all = {}; MODULE_KEYS.forEach(m => all[m.key] = true); setF({ ...f, module_permissions: all }); }} data-testid="perm-all">Tümü</Btn>
            <Btn size="sm" variant="secondary" onClick={() => setF({ ...f, module_permissions: {} })} data-testid="perm-none">Temizle</Btn>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {MODULE_KEYS.map(m => (
              <label key={m.key} className="flex items-center gap-2 text-sm bg-white p-2 border rounded">
                <input type="checkbox" checked={!!f.module_permissions?.[m.key]} onChange={e => setF({ ...f, module_permissions: { ...(f.module_permissions || {}), [m.key]: e.target.checked } })} data-testid={`perm-${m.key}`}/>
                <span>{m.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-user">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}

// iter183 — R2 storage read-only dashboard (admin only). Placed here so it
// renders at the top of the Backup tab, alongside BackupSettings.
function R2StorageCard() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/system/r2-storage-summary${refresh ? "?refresh=1" : ""}`);
      setData(r);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "R2 metrikleri şu anda alınamıyor.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(false); }, [load]);

  const fmtBytes = (n) => {
    if (n === null || n === undefined) return "—";
    if (n === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0; let v = n;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return iso.slice(0, 16).replace("T", " "); }
  };
  const fmtDateOnly = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    } catch { return iso.slice(0, 10); }
  };

  const CAT_LABELS = [
    ["photos", "Fotoğraflar"],
    ["employee_files", "Personel Dosyaları"],
    ["regulatory", "Regülasyon Belgeleri"],
    ["pif", "PIF"],
    ["sds", "SDS"],
    ["raw_material_docs", "Hammadde Belgeleri"],
    ["backups", "Yedekler"],
    ["other", "Diğer"],
  ];

  return (
    <Card className="p-4 border-sky-200 bg-sky-50/30" data-testid="r2-storage-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2">
          <HardDrive className="text-sky-700 shrink-0 mt-0.5" size={18} />
          <div>
            <div className="text-sm font-semibold text-sky-900">R2 Depolama</div>
            <div className="text-[11px] text-slate-500">
              {data?.generated_at ? `Son güncelleme: ${fmtDate(data.generated_at)}` : "Yükleniyor..."}
              {data?.cached ? ` · önbellek (${data.cache_age_seconds}s)` : ""}
            </div>
          </div>
        </div>
        <Btn size="sm" variant="secondary" onClick={() => load(true)} disabled={loading}
             data-testid="r2-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Yenile
        </Btn>
      </div>

      {error && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3"
             data-testid="r2-error">
          R2 metrikleri şu anda alınamıyor. {data?.generated_at && `Son bilinen: ${fmtDate(data.generated_at)}`}
        </div>
      )}

      {data && (
        <>
          {/* SUMMARY */}
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="p-3 bg-white border border-slate-200 rounded">
              <div className="text-[11px] text-slate-500">Toplam Kullanım</div>
              <div className="text-lg font-bold text-slate-800" data-testid="r2-total-bytes">
                {fmtBytes(data.total_bytes)}
              </div>
            </div>
            <div className="p-3 bg-white border border-slate-200 rounded">
              <div className="text-[11px] text-slate-500">Toplam Obje</div>
              <div className="text-lg font-bold text-slate-800" data-testid="r2-total-objects">
                {data.total_objects.toLocaleString("tr-TR")}
              </div>
            </div>
            <div className="p-3 bg-white border border-slate-200 rounded">
              <div className="text-[11px] text-slate-500">Durum</div>
              <div className="text-sm font-semibold flex items-center gap-1 mt-1">
                <span className={`inline-block w-2 h-2 rounded-full ${data.connected ? "bg-emerald-500" : "bg-rose-500"}`}></span>
                <span className={data.connected ? "text-emerald-700" : "text-rose-700"}
                      data-testid="r2-connection">
                  {data.connected ? "Bağlı" : "Bağlantı Yok"}
                </span>
                {data.connected && data.bucket_private && (
                  <span className="text-[10px] text-slate-500 ml-1">· Private</span>
                )}
              </div>
            </div>
          </div>

          {/* CATEGORIES */}
          <div className="mb-3">
            <div className="text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Kategori</div>
            <div className="border border-slate-200 rounded bg-white divide-y divide-slate-100">
              {CAT_LABELS.map(([code, label]) => {
                const c = data.categories?.[code] || { count: 0, bytes: 0 };
                return (
                  <div key={code} className="flex items-center justify-between px-3 py-1.5 text-xs"
                       data-testid={`r2-cat-${code}`}>
                    <div className="text-slate-700">{label}</div>
                    <div className="flex items-center gap-4 tabular-nums text-slate-600">
                      <span>{c.count.toLocaleString("tr-TR")} obje</span>
                      <span className="w-20 text-right">{fmtBytes(c.bytes)}</span>
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center justify-between px-3 py-1.5 text-xs font-semibold bg-slate-50"
                   data-testid="r2-cat-total">
                <div>TOPLAM</div>
                <div className="flex items-center gap-4 tabular-nums">
                  <span>{data.total_objects.toLocaleString("tr-TR")} obje</span>
                  <span className="w-20 text-right">{fmtBytes(data.total_bytes)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* BACKUP STATUS */}
          <div className="border border-slate-200 rounded bg-white p-3">
            <div className="text-[11px] font-semibold text-slate-600 mb-2 uppercase tracking-wide flex items-center gap-1">
              <Archive size={11} /> Yedekleme Durumu
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 text-xs">
              <div className="text-slate-500">Otomatik Yedekleme</div>
              <div className="font-semibold text-emerald-700" data-testid="r2-backup-enabled">
                {data.backup?.enabled ? "AKTİF" : "PASİF"}
              </div>
              <div className="text-slate-500">Program</div>
              <div className="tabular-nums" data-testid="r2-backup-schedule">
                Her gün {data.backup?.schedule || "—"}
              </div>
              <div className="text-slate-500">Timezone</div>
              <div data-testid="r2-backup-tz">{data.backup?.timezone || "—"}</div>
              <div className="text-slate-500">Saklama Politikası</div>
              <div data-testid="r2-backup-retention">Son {data.backup?.retention_days || "—"} gün</div>
              <div className="text-slate-500">Son Başarılı Yedek</div>
              <div className="tabular-nums" data-testid="r2-backup-last">
                {fmtDate(data.backup?.last_success)}
              </div>
              <div className="text-slate-500">Son Yedek Boyutu</div>
              <div className="tabular-nums" data-testid="r2-backup-size">
                {fmtBytes(data.backup?.last_backup_size)}
              </div>
              <div className="text-slate-500">En Eski Mevcut Yedek</div>
              <div className="tabular-nums" data-testid="r2-backup-oldest">
                {fmtDateOnly(data.backup?.oldest_backup)}
              </div>
              <div className="text-slate-500">Mevcut Yedek Sayısı</div>
              <div className="tabular-nums" data-testid="r2-backup-count">
                {data.backup?.backup_count ?? 0}
              </div>
              <div className="text-slate-500">SHA256 Doğrulama</div>
              <div className="flex items-center gap-1" data-testid="r2-backup-sha">
                {data.backup?.sha_verified ? (
                  <><CheckCircle2 size={12} className="text-emerald-600" /> Doğrulandı</>
                ) : "—"}
              </div>
            </div>
            <div className="text-[10.5px] text-slate-500 mt-2 leading-relaxed">
              Başarıyla doğrulanmış yeni yedek oluşturulduktan sonra 15 günden eski yedekler otomatik temizlenir.
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

function BackupSettings() {
  const [list, setList] = useState([]);
  const [pwStatus, setPwStatus] = useState({ is_set: false });
  const [pwInput, setPwInput] = useState("");
  const [oldPwInput, setOldPwInput] = useState("");
  const [newPwInput, setNewPwInput] = useState("");
  const [pw, setPw] = useState("");
  const [restorePw, setRestorePw] = useState("");
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // {phase, pct, detail}
  const [drOpen, setDrOpen] = useState(false);

  const load = () => {
    api.get("/backup/list").then(setList).catch(() => {});
    api.get("/backup/password/status").then(setPwStatus).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setBackupPassword = async () => {
    if (newPwInput.length < 6) { alert("Yeni parola en az 6 karakter"); return; }
    setBusy(true);
    try {
      const body = pwStatus.is_set ? { old_password: oldPwInput, new_password: newPwInput }
                                    : { new_password: newPwInput };
      const r = await api.post("/backup/password/set", body);
      alert(`✓ Parola ${pwStatus.is_set ? "değiştirildi" : "oluşturuldu"}${r.migrated ? ` (${r.migrated} yedek yeni parola ile şifrelendi).` : "."}`);
      setOldPwInput(""); setNewPwInput(""); load();
    } catch (e) { alert("Hata: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const createBackup = async () => {
    if (!pwStatus.is_set) { alert("Önce backup parolası oluşturun."); return; }
    if (!pw) { alert("Backup parolasını girin"); return; }
    setBusy(true);
    setProgress({ phase: "starting", pct: 1, detail: "başlatılıyor" });
    try {
      // iter300 — Async backup: yedekleme uzun sürdüğünde ingress/browser timeout
      // devreye giriyordu ve kullanıcı "yedek oluşturulamadı" jenerik hatası
      // alıyordu. Şimdi: (1) POST /backup/create-async — id alıp arka plana bırak,
      // (2) /backup/{id}/status — 3 sn polling, (3) verified → GET download.
      // iter301 — Progress bar + faz mesajı canlı gösteriliyor.
      // iter324 — Transient polling errors (Cloudflare parse / 502 / timeout) MUST NOT
      // immediately kill the loop; mark attempt and continue up to N consecutive misses.
      const started = await api.post("/backup/create-async", {
        password: pw, kind: "manual", name: `manual_${new Date().toISOString().slice(0,10)}`,
      });
      const bid = started.id;
      const deadline = Date.now() + 10 * 60 * 1000;
      let last = null;
      let miss = 0;
      const MAX_MISS = 10;  // ~30s of consecutive transient failures tolerated
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          last = await api.get(`/backup/${bid}/status`);
          miss = 0;  // reset on success
        } catch (e) {
          miss += 1;
          const detail = e.response?.data?.detail;
          const transient = !e.response || e.response.status >= 502 || typeof detail !== "string";
          if (!transient || miss >= MAX_MISS) {
            throw new Error(`Yedek durumu okunamadı: ${detail || e.message}`);
          }
          // transient — keep polling; show "retrying" phase so UI isn't blank
          setProgress(p => ({ phase: p?.phase || "running", pct: p?.pct || 0,
                               detail: `durum yeniden sorgulanıyor (${miss}/${MAX_MISS})` }));
          continue;
        }
        setProgress({
          phase: last?.progress_phase || last?.status || "creating",
          pct: last?.progress_pct ?? 0,
          detail: last?.progress_detail || "",
        });
        if (last?.status === "verified") break;
        if (last?.status === "failed") {
          throw new Error(last?.error || "Yedek oluşturma sırasında hata");
        }
      }
      if (!last || last.status !== "verified") {
        throw new Error("Yedekleme zaman aşımına uğradı (10 dk). Ayarlar → Yedekler listesinden manuel indirmeyi deneyin.");
      }
      setProgress({ phase: "downloading", pct: 100, detail: "indiriliyor" });
      const blob = await api.getBlob(`/backup/${bid}/download`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${last.name || "metaerp"}.metaerp`;
      a.click(); window.URL.revokeObjectURL(url);
      alert(`✓ Yedek oluşturuldu, doğrulandı ve indirildi. (${(last.size_bytes/1024/1024).toFixed(2)} MB)\n\nSHA-256:\n${last.sha256 || "—"}\n\nBu kodu kopyalayıp arşivleyin; indirdiğiniz dosyanın SHA-256'sı ile eşleşmesi dosyanın bozulmadığını gösterir.`);
      // iter319 — SHA kopyalanabilir: panoya kopyala (user prompt'u basit tut)
      try { await navigator.clipboard.writeText(last.sha256 || ""); } catch {}
      setPw(""); load();
    } catch (e) { alert("Hata: " + e.message); }
    finally { setBusy(false); setProgress(null); }
  };

  const doDownload = async (id, name) => {
    try {
      const blob = await api.getBlob(`/backup/${id}/download`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${name}.metaerp`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert("İndirme başarısız"); }
  };
  const doDelete = async (id) => {
    if (!window.confirm("Bu yedek silinsin mi?")) return;
    try { await api.del(`/backup/${id}`); load(); } catch (e) { alert("Silinemedi"); }
  };
  const doRestore = async () => {
    if (!restoreFile) { alert("Yedek dosyası seçin"); return; }
    if (!restorePw) { alert("Parolayı girin"); return; }
    if (!window.confirm(`⚠️ ONAY\n\n${restoreFile.name} yüklenecek — mod: ${restoreMode === "replace" ? "TÜM VERİYİ EZ" : "birleştir"}.\n\nDevam edilsin mi?`)) return;
    setBusy(true);
    try {
      const APIURL = process.env.REACT_APP_BACKEND_URL + "/api";
      const token = localStorage.getItem("erp_token");
      const fd = new FormData();
      fd.append("file", restoreFile); fd.append("password", restorePw); fd.append("mode", restoreMode);
      const r = await fetch(`${APIURL}/backup/restore`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Hata");
      const total = Object.values(data.restored || {}).reduce((s, n) => s + n, 0);
      alert(`✓ Geri yükleme tamamlandı: ${total} kayıt, ${Object.keys(data.restored || {}).length} koleksiyon, ${data.files || 0} dosya. Sayfayı yenileyin.`);
      setRestoreFile(null); setRestorePw("");
    } catch (e) { alert("Hata: " + e.message); }
    finally { setBusy(false); }
  };

  const fmtSize = b => !b ? "—" : (b >= 1048576 ? `${(b/1048576).toFixed(2)} MB` : `${(b/1024).toFixed(1)} KB`);
  const statusChip = (b) => {
    if (b.status === "verified") return <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">Başarılı</span>;
    if (b.status === "failed") return <span className="text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-700 font-semibold">Başarısız</span>;
    if (b.status === "creating") return <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Doğrulanıyor…</span>;
    return <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">—</span>;
  };
  const kindLabel = (b) => b.kind === "safety" ? "Safety" : (b.auto ? "Otomatik" : "Manuel");

  return (
    <div className="space-y-4">
      {/* iter184 Bug 2 fix — Iter183 R2 dashboard was defined but never rendered.
          Injecting at top of Yedekleme tab so admin sees R2 storage metrics + backup summary. */}
      <R2StorageCard />

      <Card className="p-4 border-teal-200 bg-teal-50/30">
        <div className="flex items-start gap-3">
          <Archive className="text-teal-700 shrink-0 mt-0.5" size={18}/>
          <div className="text-sm text-teal-900">
            <div className="font-semibold mb-1">Şifreli Yedekleme — AES-128 + PBKDF2 (200K iter)</div>
            <div>Tek manuel backup parolası kullanılır. Tüm otomatik ve elle alınan yedekler aynı parola ile şifrelenir. Yedekler DB koleksiyonları + audit kayıtları + backend/assets + backend/uploads dosyalarını içerir. Her yedek oluşturulunca decrypt+parse ile doğrulanır. Cron: her gece 03:00 UTC, son 30 gün rolling saklama.</div>
          </div>
        </div>
      </Card>

      {/* BACKUP PAROLASI */}
      <Card className="p-4">
        <div className="text-sm font-semibold mb-2 flex items-center gap-2">
          <KeyRound size={14}/> Backup Parolası
          {pwStatus.is_set && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Kurulu</span>}
          {!pwStatus.is_set && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800">Henüz Kurulmadı</span>}
        </div>
        {pwStatus.is_set && (
          <div className="text-[11px] text-slate-500 mb-2">Son güncelleme: {(pwStatus.updated_at || "").slice(0,19).replace("T"," ")} — {pwStatus.updated_by || "—"}</div>
        )}
        <div className="grid md:grid-cols-3 gap-2 items-end">
          {pwStatus.is_set && (
            <Input label="Mevcut Parola" type="password" value={oldPwInput} onChange={e => setOldPwInput(e.target.value)} data-testid="bp-old"/>
          )}
          <Input label={pwStatus.is_set ? "Yeni Parola" : "Yeni Backup Parolası"} type="password" value={newPwInput} onChange={e => setNewPwInput(e.target.value)} data-testid="bp-new"/>
          <Btn onClick={setBackupPassword} disabled={busy} data-testid="bp-save"><KeyRound size={12}/> {pwStatus.is_set ? "Parolayı Değiştir" : "Parolayı Oluştur"}</Btn>
        </div>
        {pwStatus.is_set && (
          <div className="text-[11px] text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠️ Parola değişince mevcut tüm sunucu yedekleri otomatik olarak yeni parola ile yeniden şifrelenir. Herhangi bir migrasyon başarısız olursa değişiklik iptal edilir.
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="text-sm font-semibold mb-2 flex items-center gap-1"><Archive size={14}/> Yeni Yedek Oluştur</div>
          <Input label="Backup Parolası" type="password" value={pw} onChange={e => setPw(e.target.value)} data-testid="backup-pw"/>
          <div className="text-[11px] text-slate-500 mb-3">Sistem parolasıyla doğrulanır. Verify + rolling retention otomatik.</div>
          <Btn onClick={createBackup} disabled={busy || !pwStatus.is_set} data-testid="backup-create"><Archive size={12}/> {busy ? "Yedekleniyor..." : "Yedek Al, Doğrula ve İndir"}</Btn>
          {busy && progress && (
            <div className="mt-3 border border-emerald-200 bg-emerald-50/50 rounded p-2" data-testid="backup-progress">
              <div className="flex justify-between text-[11px] text-slate-700 mb-1">
                <span className="font-semibold">
                  {({
                    starting: "Başlatılıyor",
                    dumping: "Veritabanı çıkarılıyor",
                    collecting_files: "Dosyalar toplanıyor",
                    compressing: "Sıkıştırılıyor",
                    encrypting: "Şifreleniyor",
                    verifying: "Doğrulanıyor",
                    uploading_r2: "Cloudflare R2'ye yükleniyor",
                    finalizing: "Kayıtlar güncelleniyor",
                    downloading: "İndiriliyor",
                    verified: "Tamamlandı",
                  })[progress.phase] || progress.phase}
                </span>
                <span className="tabular-nums" data-testid="backup-progress-pct">%{progress.pct ?? 0}</span>
              </div>
              <div className="h-1.5 bg-emerald-100 rounded overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${Math.max(2, Math.min(100, progress.pct ?? 0))}%` }}/>
              </div>
              {progress.detail && <div className="text-[10px] text-slate-500 italic mt-1 truncate" data-testid="backup-progress-detail">{progress.detail}</div>}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="text-sm font-semibold mb-2 flex items-center gap-1"><Upload size={14}/> Yedek Geri Yükle</div>
          <input type="file" accept=".metaerp" onChange={e => setRestoreFile(e.target.files[0])} className="text-xs mb-2 w-full" data-testid="backup-file"/>
          <Input label="Yedek Parolası" type="password" value={restorePw} onChange={e => setRestorePw(e.target.value)} data-testid="backup-restore-pw"/>
          <Select label="Mod" value={restoreMode} onChange={e => setRestoreMode(e.target.value)} data-testid="backup-mode">
            <option value="merge">Birleştir (mevcut kayıtları koru, ekle/güncelle)</option>
            <option value="replace">Tümüyle Değiştir (⚠️ mevcut verileri sil)</option>
          </Select>
          <div className="mt-2"><Btn variant="secondary" onClick={doRestore} disabled={busy} data-testid="backup-restore"><Upload size={12}/> {busy ? "Yükleniyor..." : "Geri Yükle"}</Btn></div>
        </Card>
      </div>

      {/* FELAKET KURTARMA */}
      <Card className="p-4 border-rose-300 bg-rose-50/40">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-rose-800 flex items-center gap-2">
              <AlertTriangle size={16}/> Felaket Kurtarma
            </div>
            <div className="text-[11px] text-rose-700 mt-1">Sistemi son sağlam yedeğe döndürür. Restore öncesi güvenlik yedeği alınır. Sadece Sistem Yetkilisi.</div>
          </div>
          <Btn variant="danger" onClick={() => setDrOpen(true)} data-testid="dr-open"><AlertTriangle size={14}/> Sistemi Kurtar</Btn>
        </div>
      </Card>

      <Card className="p-4">
        <div className="text-sm font-semibold mb-2">Saklanan Yedekler ({list.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="backup-list">
            <thead className="bg-slate-50 border-b">
              <tr>{["Tarih/Saat", "Ad", "Tip", "Boyut", "Durum", "Doğrulandı", "İşlem"].map((h, i) =>
                <th key={i} className="text-left px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10px]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400">Kayıt yok</td></tr>}
              {list.map(b => (
                <tr key={b.id} className="border-b hover:bg-slate-50">
                  <td className="px-2 py-1.5 font-mono">{(b.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-2 py-1.5">{b.name}</td>
                  <td className="px-2 py-1.5"><span className={`text-[10px] px-2 py-0.5 rounded ${b.kind === "safety" ? "bg-orange-100 text-orange-700" : b.auto ? "bg-cyan-100 text-cyan-700" : "bg-fuchsia-100 text-fuchsia-700"}`}>{kindLabel(b)}</span></td>
                  <td className="px-2 py-1.5">{fmtSize(b.size_bytes || 0)}</td>
                  <td className="px-2 py-1.5">{statusChip(b)}</td>
                  <td className="px-2 py-1.5">{b.verified ? <span className="text-emerald-600 font-semibold">✓ Evet</span> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1">
                      <Btn size="sm" variant="secondary" onClick={() => doDownload(b.id, b.name)} data-testid={`backup-dl-${b.id}`}><Download size={10}/></Btn>
                      <Btn size="sm" variant="danger" onClick={() => doDelete(b.id)} data-testid={`backup-del-${b.id}`}><Trash2 size={10}/></Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {drOpen && <DisasterRecoveryModal onClose={() => setDrOpen(false)} onDone={() => { setDrOpen(false); load(); }}/>}
    </div>
  );
}

function DisasterRecoveryModal({ onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [loginPw, setLoginPw] = useState("");
  const [backupPw, setBackupPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/disaster-recovery/last-good-backup")
      .then(setPreview)
      .catch(e => setErr(e.response?.data?.detail || e.message));
  }, []);

  const run = async () => {
    if (!loginPw || !backupPw) { alert("Login ve Backup parolalarını giriniz."); return; }
    if (!window.confirm("⚠️ KRİTİK\n\nSistem seçilen yedeğin tarihine geri döndürülecek. Restore öncesi safety backup otomatik alınacak.\n\nDevam edilsin mi?")) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/disaster-recovery/run", { login_password: loginPw, backup_password: backupPw });
      setResult(r);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="dr-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full mx-4">
        <div className="px-4 py-3 border-b bg-rose-50 flex items-center justify-between">
          <div className="font-bold text-rose-800 flex items-center gap-2"><AlertTriangle size={16}/> Sistemi Kurtar</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          {result ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-emerald-800" data-testid="dr-success">
              <div className="font-semibold mb-2">✓ Kurtarma tamamlandı</div>
              <div className="text-xs space-y-1">
                <div>Restore edildi: <b>{Object.keys(result.restored?.restored || {}).length} koleksiyon</b>, <b>{result.restored?.files || 0} dosya</b></div>
                <div>Safety backup: <span className="font-mono">{result.safety_backup_id}</span></div>
                <div>Başlangıç: {result.started_at?.slice(0,19).replace("T"," ")}</div>
                <div>Bitiş: {result.ended_at?.slice(0,19).replace("T"," ")}</div>
              </div>
              <Btn className="mt-3" onClick={onDone}>Kapat & Sayfayı Yenile</Btn>
            </div>
          ) : (
            <>
              <div className="bg-slate-50 rounded p-3">
                <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Son Sağlam Yedek</div>
                {preview ? (
                  <div className="text-xs space-y-1" data-testid="dr-preview">
                    <div><b>Ad:</b> {preview.name}</div>
                    <div><b>Tarih:</b> {(preview.created_at || "").slice(0,19).replace("T"," ")}</div>
                    <div><b>Boyut:</b> {((preview.size_bytes || 0)/1048576).toFixed(2)} MB</div>
                    <div><b>Doğrulandı:</b> <span className="text-emerald-600 font-semibold">✓ Başarılı</span></div>
                  </div>
                ) : (
                  <div className="text-xs text-rose-700">{err || "Yükleniyor..."}</div>
                )}
              </div>
              <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-2">
                Sistem bu yedekleme tarihindeki duruma geri döndürülecektir. Restore öncesi otomatik güvenlik yedeği alınacak, herhangi bir hata durumunda otomatik rollback devreye girer.
              </div>
              <Input label="Login Parolanız" type="password" value={loginPw} onChange={e => setLoginPw(e.target.value)} data-testid="dr-login-pw"/>
              <Input label="Backup Parolası" type="password" value={backupPw} onChange={e => setBackupPw(e.target.value)} data-testid="dr-backup-pw"/>
              {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="dr-err">{err}</div>}
              <div className="flex justify-end gap-2">
                <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
                <Btn variant="danger" onClick={run} disabled={busy || !preview} data-testid="dr-run">
                  <AlertTriangle size={12}/> {busy ? "Kurtarılıyor..." : "Sistemi Kurtar"}
                </Btn>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
/* ============ Tarih-Filtreli Toplu Veri Temizleme (Msg 244) ============ */
function BulkCleanupSection({ collections, reloadColls, reloadTrash }) {
  const [selected, setSelected] = useState({});
  const [filterType, setFilterType] = useState("hours");
  const [x, setX] = useState(2);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = () => api.get("/admin/bulk-cleanup/history").then(setHistory).catch(() => {});
  React.useEffect(() => { loadHistory(); }, []);

  const toggleAll = (val) => { const o = {}; collections.forEach(c => { o[c.key] = val; }); setSelected(o); };
  const runPreview = async () => {
    const keys = Object.keys(selected).filter(k => selected[k]);
    if (keys.length === 0) { alert("En az bir modül seçin"); return; }
    if (filterType !== "all" && (!x || x <= 0)) { alert("X pozitif tam sayı olmalı"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/bulk-cleanup/preview", { collections: keys, filter_type: filterType, x: parseInt(x) });
      setPreview(r); setConfirmText("");
    } catch (e) { alert(e.response?.data?.detail || "Önizleme hatası"); }
    finally { setBusy(false); }
  };
  const runExecute = async () => {
    if (filterType === "all" && confirmText !== "TEMIZLE") { alert("'TEMIZLE' yazıp onaylamanız gerekir"); return; }
    if (!window.confirm(`Toplam ${preview.total} kayıt geri alınacak/silinecek. Bu işlem envanter, muhasebe ve stok bakiyelerini etkileyebilir. Devam edilsin mi?`)) return;
    const keys = Object.keys(selected).filter(k => selected[k]);
    setBusy(true);
    try {
      const r = await api.post("/admin/bulk-cleanup/execute", { collections: keys, filter_type: filterType, x: parseInt(x), confirmation: confirmText });
      alert(`Batch ${r.batch_no} — Toplam ${r.total_moved} kayıt taşındı, ${r.reversal_ops?.length || 0} envanter işlemi geri alındı.`);
      setSelected({}); setPreview(null); setConfirmText("");
      reloadColls(); reloadTrash(); loadHistory();
    } catch (e) { alert(e.response?.data?.detail || "Temizleme hatası"); }
    finally { setBusy(false); }
  };
  // Direct-delete button (önizleme adımı olmadan). Kullanıcı isteği:
  // "Önizle butonu kalmaya devam etsin, hemen yanında bir Sil butonu olsun,
  // tıklayınca son bir onay uyarısı ver, onaylayınca seçilen zaman/modülde
  // veriler silinsin." Yine güvenlik için arka planda preview çekip toplam
  // sayıyı onay diyaloğunda gösteriyoruz, 'Tüm Zamanlar' için ekstra TEMIZLE
  // onayı istiyoruz — hiçbir güvenlik katmanını atlamıyoruz.
  const runDirectDelete = async () => {
    const keys = Object.keys(selected).filter(k => selected[k]);
    if (keys.length === 0) { alert("En az bir modül seçin"); return; }
    if (filterType !== "all" && (!x || x <= 0)) { alert("X pozitif tam sayı olmalı"); return; }
    setBusy(true);
    try {
      // 1) Önizleme al — kaç kayıt etkilenecek onu göstermek için
      const p = await api.post("/admin/bulk-cleanup/preview", { collections: keys, filter_type: filterType, x: parseInt(x) });
      setPreview(p);
      if (!p.total || p.total === 0) { alert("Silinecek kayıt bulunamadı."); return; }
      const rangeTxt = p.since ? `${p.since.slice(0, 16).replace("T", " ")} → ${p.until.slice(0, 16).replace("T", " ")}` : "TÜM ZAMANLAR";
      const moduleTxt = (p.per_module || []).filter(m => m.count > 0).map(m => `• ${m.label}: ${m.count}`).join("\n");
      let cText = "";
      // 2) 'Tüm Zamanlar' için ekstra prompt (TEMIZLE yaz)
      if (filterType === "all") {
        const typed = window.prompt(`⚠ TÜM ZAMANLAR seçildi.\n\nToplam ${p.total} kayıt kalıcı olarak silinecek.\n\n${moduleTxt}\n\nOnaylamak için 'TEMIZLE' yazın:`);
        if ((typed || "").trim().toUpperCase() !== "TEMIZLE") { alert("Onay iptal edildi."); return; }
        cText = "TEMIZLE"; setConfirmText("TEMIZLE");
      } else {
        // 3) Son onay — kaç kayıt + hangi modüller + zaman aralığı
        if (!window.confirm(`⚠ SON ONAY — Silme İşlemi\n\nZaman aralığı: ${rangeTxt}\n\n${moduleTxt}\n\nTOPLAM: ${p.total} kayıt\n\nBu işlem envanter, muhasebe ve stok bakiyelerini geri çevirebilir. Kayıtlar 30 gün çöp kutusunda tutulur ve geri yüklenebilir.\n\nDevam edilsin mi?`)) return;
      }
      // 4) Execute
      const r = await api.post("/admin/bulk-cleanup/execute", { collections: keys, filter_type: filterType, x: parseInt(x), confirmation: cText });
      alert(`✓ Batch ${r.batch_no} — Toplam ${r.total_moved} kayıt silindi (çöp kutusuna taşındı), ${r.reversal_ops?.length || 0} envanter işlemi geri alındı.`);
      setSelected({}); setPreview(null); setConfirmText("");
      reloadColls(); reloadTrash(); loadHistory();
    } catch (e) { alert(e.response?.data?.detail || "Silme hatası"); }
    finally { setBusy(false); }
  };
  const doRestore = async (b) => {
    if (!window.confirm(`Batch ${b.batch_no} tamamen geri yüklensin mi? Envanter değişiklikleri ve silinen kayıtlar orijinal yerine döndürülecek.`)) return;
    try { const r = await api.post(`/admin/bulk-cleanup/restore/${b.id}`); alert(`${r.restored} kayıt ${b.batch_no} batch'inden geri yüklendi`); loadHistory(); reloadColls(); reloadTrash(); }
    catch (e) { alert(e.response?.data?.detail || "Geri yükleme hatası"); }
  };

  return (
    <Card className="p-4 border-red-200 bg-red-50/30">
      <div className="mb-4 flex items-start gap-3 p-3 border border-red-300 bg-red-50 rounded">
        <ShieldAlert className="text-red-600 shrink-0 mt-0.5" size={18}/>
        <div className="text-sm text-red-800">
          <div className="font-semibold mb-1">Tarih Filtreli Toplu Veri Temizleme — Güvenli Rollback</div>
          <div>Seçilen zaman aralığında oluşturulan kayıtlar çöp kutusuna taşınır (30 gün geri alma). <b>Envanter Hareketleri</b> seçildiğinde ilgili stok miktarları da otomatik ters çevrilir. Her işlem bir <b>Cleanup Batch</b> altında loglanır — dilerseniz batch'i tümüyle geri yükleyebilirsiniz.</div>
        </div>
      </div>

      {/* 1. Zaman filtresi */}
      <div className="mb-4 p-3 bg-white rounded border border-slate-200">
        <div className="text-xs font-semibold text-slate-700 uppercase mb-2">1. Zaman Aralığı</div>
        <div className="flex flex-wrap gap-3 items-end">
          {[
            { k: "hours", l: "Son X Saat" },
            { k: "days",  l: "Son X Gün"  },
            { k: "weeks", l: "Son X Hafta"},
            { k: "all",   l: "Tüm Zamanlar" },
          ].map(f => (
            <label key={f.k} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded border cursor-pointer text-sm ${filterType === f.k ? "border-red-500 bg-red-50 text-red-700 font-semibold" : "border-slate-200 hover:border-red-300"}`}>
              <input type="radio" name="cleanup-filter" checked={filterType === f.k} onChange={() => setFilterType(f.k)} data-testid={`cleanup-filter-${f.k}`}/>
              {f.l}
            </label>
          ))}
          {filterType !== "all" && (
            <div>
              <label className="text-[10px] uppercase text-slate-500">X (pozitif tam sayı)</label>
              <input type="number" min="1" value={x} onChange={e => setX(e.target.value)} className="w-24 px-2 py-1.5 text-sm border border-slate-300 rounded font-mono" data-testid="cleanup-x"/>
            </div>
          )}
        </div>
      </div>

      {/* 2. Modül seç */}
      <div className="mb-4 p-3 bg-white rounded border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-700 uppercase">2. Modülleri Seç</div>
          <div className="flex gap-2">
            <Btn size="sm" variant="secondary" onClick={() => toggleAll(true)} data-testid="cleanup-select-all">Tümünü Seç</Btn>
            <Btn size="sm" variant="secondary" onClick={() => toggleAll(false)} data-testid="cleanup-select-none">Temizle</Btn>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {collections.map(c => (
            <label key={c.key} className="flex items-center gap-2 text-sm bg-slate-50 p-2 border rounded hover:border-red-300">
              <input type="checkbox" checked={!!selected[c.key]} onChange={e => setSelected({ ...selected, [c.key]: e.target.checked })} data-testid={`cleanup-cb-${c.key}`}/>
              <span className="flex-1 truncate" title={c.label}>{c.label}</span>
              <span className="text-xs text-slate-500 font-mono">{c.count}</span>
            </label>
          ))}
        </div>
      </div>

      {/* 3. Önizle + Sil (yan yana) — kullanıcı isteği msg-cleanup-delete */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Btn onClick={runPreview} disabled={busy} data-testid="cleanup-preview"><RefreshCw size={14}/> {busy ? "Analiz Ediliyor..." : "Etkilenecek Verileri Göster (Önizle)"}</Btn>
        <Btn variant="danger" onClick={runDirectDelete} disabled={busy} data-testid="cleanup-delete-direct"><Trash2 size={14}/> Sil</Btn>
        <Btn variant="secondary" size="sm" onClick={() => setShowHistory(!showHistory)}>{showHistory ? "Geçmişi Gizle" : `Temizleme Geçmişi (${history.length})`}</Btn>
      </div>

      {preview && (
        <div className="mb-4 p-3 rounded border-2 border-amber-300 bg-amber-50" data-testid="cleanup-preview-result">
          <div className="font-bold text-amber-900 text-sm mb-2">⚠ Önizleme — hiçbir veri henüz değiştirilmedi</div>
          <div className="text-xs text-slate-700 mb-2">
            <b>Zaman aralığı:</b> {preview.since ? `${preview.since.slice(0, 16).replace("T", " ")} → ${preview.until.slice(0, 16).replace("T", " ")}` : "TÜM ZAMANLAR"}
          </div>
          <table className="w-full text-xs bg-white rounded overflow-hidden">
            <thead className="bg-slate-100"><tr><th className="px-2 py-1 text-left">Modül</th><th className="px-2 py-1 text-right">Etkilenecek Kayıt</th></tr></thead>
            <tbody>
              {preview.per_module.map(m => (
                <tr key={m.key} className="border-t"><td className="px-2 py-1">{m.label}</td><td className="px-2 py-1 text-right font-mono">{m.count}</td></tr>
              ))}
              <tr className="bg-amber-100 font-bold"><td className="px-2 py-1">TOPLAM</td><td className="px-2 py-1 text-right font-mono" data-testid="cleanup-total">{preview.total}</td></tr>
            </tbody>
          </table>
          {(preview.warnings || []).length > 0 && (
            <div className="mt-2 space-y-1">
              {preview.warnings.map((w, i) => <div key={i} className="text-[11px] p-2 rounded bg-orange-100 border border-orange-300 text-orange-900" data-testid={`cleanup-warn-${i}`}>⚠ {w}</div>)}
            </div>
          )}
          {(preview.inventory_impact || []).length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer font-semibold text-slate-700">Envanter Ters İşlem Önizlemesi ({preview.inventory_impact.length} malzeme)</summary>
              <div className="mt-1 max-h-40 overflow-y-auto bg-white border rounded p-2">
                {preview.inventory_impact.map((imp, i) => (
                  <div key={i} className="flex justify-between text-[11px] border-b py-0.5"><span className="font-mono">{imp.material_id.slice(0, 12)}...</span><span className={`font-mono ${imp.qty_delta > 0 ? "text-emerald-700" : "text-red-700"}`}>{imp.qty_delta > 0 ? "+" : ""}{imp.qty_delta}</span></div>
                ))}
              </div>
            </details>
          )}
          {preview.cascade_preview && (preview.cascade_preview.raw_returns > 0 || preview.cascade_preview.finished_lot_removals > 0 || preview.cascade_preview.shipment_restocks > 0) && (
            <div className="mt-2 p-2 rounded bg-blue-50 border border-blue-300 text-xs" data-testid="cleanup-cascade">
              <div className="font-bold text-blue-900 mb-1">🔗 Kaskad Reversal Etkisi</div>
              {preview.cascade_preview.raw_returns > 0 && <div>• <b>{preview.cascade_preview.raw_returns}</b> hammadde tartımı ilgili lotlara iade edilecek</div>}
              {preview.cascade_preview.finished_lot_removals > 0 && <div>• <b>{preview.cascade_preview.finished_lot_removals}</b> bitmiş ürün lot'u envanterden düşürülecek</div>}
              {preview.cascade_preview.shipment_restocks > 0 && <div>• <b>{preview.cascade_preview.shipment_restocks}</b> sevkiyat kalemi bitmiş ürün stoğuna geri alınacak</div>}
            </div>
          )}
          {filterType === "all" && (
            <div className="mt-3 p-2 rounded bg-red-100 border border-red-400">
              <div className="text-xs font-bold text-red-800 mb-1">'Tüm Zamanlar' seçildi — Ekstra Onay Gerekli</div>
              <div className="flex gap-2 items-center">
                <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="TEMIZLE yazın" className="flex-1 px-2 py-1 text-sm border-2 border-red-400 rounded font-bold uppercase" data-testid="cleanup-confirm-text"/>
              </div>
            </div>
          )}
          <div className="mt-3">
            <Btn variant="danger" onClick={runExecute} disabled={busy || preview.total === 0} data-testid="cleanup-execute"><Trash2 size={14}/> Seçilen Verileri Temizle ve Geri Al</Btn>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="p-3 rounded border border-slate-200 bg-white" data-testid="cleanup-history">
          <div className="text-xs font-semibold text-slate-700 uppercase mb-2">Temizleme Geçmişi</div>
          <table className="w-full text-xs">
            <thead className="bg-slate-100"><tr>
              <th className="text-left px-2 py-1">İşlem No</th>
              <th className="text-left px-2 py-1">Tarih</th>
              <th className="text-left px-2 py-1">Kullanıcı</th>
              <th className="text-left px-2 py-1">Filtre</th>
              <th className="text-left px-2 py-1">Modüller</th>
              <th className="text-right px-2 py-1">Kayıt</th>
              <th className="text-left px-2 py-1">Durum</th>
              <th className="text-left px-2 py-1">İşlem</th>
            </tr></thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={8} className="text-center py-4 text-slate-400">Henüz temizleme yok.</td></tr>}
              {history.map(b => (
                <tr key={b.id} className="border-t hover:bg-slate-50">
                  <td className="px-2 py-1 font-mono">{b.batch_no}</td>
                  <td className="px-2 py-1 font-mono">{(b.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-2 py-1">{b.user_name}</td>
                  <td className="px-2 py-1">{b.filter_type === "all" ? "Tüm Zamanlar" : `Son ${b.x} ${b.filter_type}`}</td>
                  <td className="px-2 py-1 text-[10px] text-slate-500 truncate max-w-[150px]">{(b.modules || []).join(", ")}</td>
                  <td className="px-2 py-1 text-right font-mono">{b.total_moved}</td>
                  <td className="px-2 py-1"><Badge level={b.status === "RESTORED" ? "attention" : "ok"}>{b.status}</Badge></td>
                  <td className="px-2 py-1">
                    {b.status !== "RESTORED" && <Btn size="sm" variant="secondary" onClick={() => doRestore(b)} data-testid={`cleanup-restore-${b.batch_no}`}>Geri Yükle</Btn>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}



export function SettingsPage() {
  const { isAdmin } = useAuth();
  const [s, setS] = useState({});
  const [tab, setTab] = useState("general");
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [trash, setTrash] = useState([]);
  const [trashFilter, setTrashFilter] = useState("");
  const [restoreTarget, setRestoreTarget] = useState(null);
  useEffect(() => { api.get("/settings").then(setS); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/bulk-delete/collections").then(setCollections).catch(() => {});
    api.get("/trash").then(setTrash).catch(() => {});
  }, [isAdmin]);
  const reloadTrash = () => api.get("/trash").then(setTrash).catch(() => {});
  const reloadColls = () => api.get("/admin/bulk-delete/collections").then(setCollections).catch(() => {});
  const save = async () => { await api.put("/settings", s); alert("Ayarlar kaydedildi"); };
  const toggleAll = (val) => {
    const o = {}; collections.forEach(c => { o[c.key] = val; }); setSelected(o);
  };
  const runBulk = async () => {
    const keys = Object.keys(selected).filter(k => selected[k]);
    if (keys.length === 0) { alert("En az bir platform seçin"); return; }
    if (!window.confirm(`⚠️ UYARI\n\nSeçili ${keys.length} platformdaki TÜM veriler ÇÖP KUTUSUNA taşınacak.\n\n30 gün içinde "Çöp Kutusu" sekmesinden geri yükleyebilirsiniz. 30 gün sonra kalıcı olarak silinirler.\n\nDevam edilsin mi?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/bulk-delete", { collections: keys });
      alert(`Toplam ${r.total_deleted} kayıt çöp kutusuna taşındı. Geri yükleme: Çöp Kutusu sekmesi.`);
      setSelected({}); reloadColls(); reloadTrash();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };
  const restore = (t) => setRestoreTarget(t);
  const confirmRestore = async () => {
    const t = restoreTarget;
    if (!t) return;
    try {
      await api.post(`/trash/${t.id}/restore`);
      setRestoreTarget(null);
      reloadTrash(); reloadColls();
      alert("Geri yüklendi.");
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const purgeOne = async (t) => {
    if (!window.confirm(`⚠️ "${t.entity_name}" kaydı KALICI olarak silinsin mi?\n\nBu işlem geri alınamaz.`)) return;
    try { await api.del(`/trash/${t.id}`); reloadTrash(); }
    catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const purgeAll = async () => {
    if (!window.confirm(`⚠️ UYARI\n\nÇöp kutusundaki TÜM ${trash.length} kayıt KALICI olarak silinecek.\n\nBu işlem geri alınamaz. Devam edilsin mi?`)) return;
    if (!window.confirm("Son onay: Tüm çöp kutusu boşaltılacak. Emin misiniz?")) return;
    try { const r = await api.post("/trash/purge-all"); alert(`${r.purged} kayıt kalıcı silindi.`); reloadTrash(); }
    catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const purgeExpired = async () => {
    try { const r = await api.post("/trash/purge-expired"); alert(`${r.purged} süresi dolmuş kayıt silindi (>${r.retention_days} gün).`); reloadTrash(); }
    catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const filteredTrash = trash.filter(t => !trashFilter || t.entity_name === trashFilter);
  const trashEntities = Array.from(new Set(trash.map(t => t.entity_name))).sort();
  const daysLeft = (t) => {
    if (!t.expires_at) return "—";
    const ms = new Date(t.expires_at).getTime() - Date.now();
    const d = Math.max(0, Math.floor(ms / 86400000));
    return `${d} gün`;
  };

  return (
    <div>
      <PageHeader title="Sistem Ayarları" description="Şirket bilgileri, format ayarları ve veri yönetimi" />
      <div className="border-b border-slate-200 mb-4 flex gap-4">
        <button onClick={() => setTab("general")} data-testid="settings-tab-general"
          className={`px-1 py-2 text-sm border-b-2 ${tab === "general" ? "border-indigo-600 text-indigo-700 font-medium" : "border-transparent text-slate-500"}`}>
          Genel
        </button>
        {isAdmin && (
          <button onClick={() => setTab("bulk")} data-testid="settings-tab-bulk"
            className={`px-1 py-2 text-sm border-b-2 flex items-center gap-2 ${tab === "bulk" ? "border-red-600 text-red-700 font-medium" : "border-transparent text-slate-500"}`}>
            <ShieldAlert size={14}/> Toplu Silme
          </button>
        )}
        {isAdmin && (
          <button onClick={() => setTab("trash")} data-testid="settings-tab-trash"
            className={`px-1 py-2 text-sm border-b-2 flex items-center gap-2 ${tab === "trash" ? "border-amber-600 text-amber-700 font-medium" : "border-transparent text-slate-500"}`}>
            <Trash2 size={14}/> Çöp Kutusu ({trash.length})
          </button>
        )}
        <button onClick={() => setTab("telegram")} data-testid="settings-tab-telegram"
          className={`px-1 py-2 text-sm border-b-2 flex items-center gap-2 ${tab === "telegram" ? "border-sky-600 text-sky-700 font-medium" : "border-transparent text-slate-500"}`}>
          <Send size={14}/> Telegram Bot
        </button>
        {isAdmin && (
          <button onClick={() => setTab("email")} data-testid="settings-tab-email"
            className={`px-1 py-2 text-sm border-b-2 flex items-center gap-2 ${tab === "email" ? "border-fuchsia-600 text-fuchsia-700 font-medium" : "border-transparent text-slate-500"}`}>
            <Mail size={14}/> E-posta Sağlayıcı
          </button>
        )}
        {isAdmin && (
          <button onClick={() => setTab("backup")} data-testid="settings-tab-backup"
            className={`px-1 py-2 text-sm border-b-2 flex items-center gap-2 ${tab === "backup" ? "border-teal-600 text-teal-700 font-medium" : "border-transparent text-slate-500"}`}>
            <Archive size={14}/> Yedekleme
          </button>
        )}
        {isAdmin && (
          <button onClick={() => setTab("security")} data-testid="settings-tab-security"
            className={`px-1 py-2 text-sm border-b-2 flex items-center gap-2 ${tab === "security" ? "border-emerald-600 text-emerald-700 font-medium" : "border-transparent text-slate-500"}`}>
            <ShieldCheck size={14}/> Güvenlik (MFA)
          </button>
        )}
      </div>
      {tab === "backup" && isAdmin && <BackupSettings />}
      {tab === "security" && isAdmin && <SecurityMFA />}
      {tab === "telegram" && <TelegramSettings isAdmin={isAdmin} />}
      {tab === "email" && isAdmin && <EmailProviderSettings />}
      {tab === "general" && (
        <Card className="p-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Şirket Adı" value={s.company_name || ""} onChange={e => setS({ ...s, company_name: e.target.value })} />
            <Input label="Varsayılan Para Birimi" value={s.currency || ""} onChange={e => setS({ ...s, currency: e.target.value })} />
            <Input label="KDV Oranı" type="number" value={s.vat_rate || 20} onChange={e => setS({ ...s, vat_rate: parseFloat(e.target.value) })} />
            <Input label="Lot Format" value={s.lot_format || ""} onChange={e => setS({ ...s, lot_format: e.target.value })} />
            <Input label="Proses Fire Toleransı %" type="number" step="0.1" value={s.default_process_loss_pct || 1} onChange={e => setS({ ...s, default_process_loss_pct: parseFloat(e.target.value) })} />
            <Input label="Dolum Fire Toleransı %" type="number" step="0.1" value={s.default_fill_loss_pct || 2} onChange={e => setS({ ...s, default_fill_loss_pct: parseFloat(e.target.value) })} />
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <Mail size={16} className="text-emerald-600" />
              <h3 className="text-sm font-semibold text-slate-800">Muhasebeci Ayarları</h3>
              <span className="text-xs text-slate-500">Aylık personel muhasebe bildirimi için varsayılan alıcı</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Muhasebeci E-posta Adresi"
                     value={s.accountant_email || ""}
                     onChange={e => setS({ ...s, accountant_email: e.target.value })}
                     data-testid="settings-accountant-email"
                     placeholder="muhasebe@ornek.com" />
              <Input label="Muhasebeci Telegram Chat ID (opsiyonel)"
                     value={s.accountant_telegram_chat_id || ""}
                     onChange={e => setS({ ...s, accountant_telegram_chat_id: e.target.value })}
                     data-testid="settings-accountant-telegram"
                     placeholder="-100... veya numeric" />
            </div>
            <label className="flex items-start gap-2 mt-3 text-sm cursor-pointer"
                   data-testid="settings-accountant-auto-toggle-label">
              <input type="checkbox"
                     checked={!!s.accountant_auto_email_enabled}
                     onChange={e => {
                       const v = e.target.checked;
                       if (v && !(s.accountant_email || "").includes("@")) {
                         alert("Otomatik gönderim için geçerli bir muhasebeci e-posta adresi gerekiyor.");
                         return;
                       }
                       setS({ ...s, accountant_auto_email_enabled: v });
                     }}
                     data-testid="settings-accountant-auto-toggle"
                     className="mt-0.5" />
              <span>
                <span className="font-medium">Aylık Muhasebe Raporunu Otomatik E-posta Gönder</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Sistem her ayın son takvim gününde saat <b>23:59 Europe/Istanbul</b> raporu muhasebeci e-postasına otomatik gönderir. Aynı dönem tekrar gönderilmez.
                </span>
              </span>
            </label>
          </div>

          <div className="mt-4"><Btn onClick={save} data-testid="save-settings">Kaydet</Btn></div>
        </Card>
      )}
      {tab === "bulk" && isAdmin && (
        <BulkCleanupSection collections={collections} reloadColls={reloadColls} reloadTrash={reloadTrash} />
      )}
      {tab === "trash" && isAdmin && (
        <Card className="p-4 border-amber-200 bg-amber-50/30">
          <div className="mb-4 flex items-start gap-3 p-3 border border-amber-300 bg-amber-50 rounded">
            <Trash2 className="text-amber-700 shrink-0 mt-0.5" size={18}/>
            <div className="text-sm text-amber-900">
              <div className="font-semibold mb-1">Çöp Kutusu — 30 Gün Saklama</div>
              <div>Silinen kayıtlar burada 30 gün saklanır. "Geri Yükle" ile kayıt orijinal yerine döndürülür. "Kalıcı Sil" veya süresi dolan kayıtlar geri alınamaz.</div>
            </div>
          </div>
          <div className="flex gap-2 mb-3 items-center flex-wrap">
            <select value={trashFilter} onChange={e => setTrashFilter(e.target.value)} className="px-2 py-1 text-sm border rounded" data-testid="trash-filter">
              <option value="">{`Tüm Kayıtlar (${trash.length})`}</option>
              {trashEntities.map(e => <option key={e} value={e}>{`${e} (${trash.filter(t => t.entity_name === e).length})`}</option>)}
            </select>
            <Btn size="sm" variant="secondary" onClick={reloadTrash} data-testid="trash-refresh">Yenile</Btn>
            <Btn size="sm" variant="secondary" onClick={purgeExpired} data-testid="trash-purge-expired">Süresi Dolanları Temizle</Btn>
            <Btn size="sm" variant="danger" onClick={purgeAll} disabled={trash.length === 0} data-testid="trash-purge-all">Çöp Kutusunu Boşalt</Btn>
          </div>
          <div className="overflow-x-auto bg-white border border-slate-200 rounded">
            <table className="w-full text-xs" data-testid="trash-table">
              <thead className="bg-slate-50/70 border-b">
                <tr>
                  {["Silinme Tarihi", "Modül", "Açıklama", "Silen Kullanıcı", "Kalan Süre", "İşlem"].map(h => (
                    <th key={h} className="text-left px-2 py-2 text-[11px] font-semibold text-slate-600 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTrash.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Çöp kutusu boş</td></tr>}
                {filteredTrash.map(t => (
                  <tr key={t.id} className="border-b hover:bg-slate-50">
                    <td className="px-2 py-1.5 font-mono">{(t.deleted_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-2 py-1.5"><Badge level="attention">{t.entity_name}</Badge></td>
                    <td className="px-2 py-1.5 text-slate-800" data-testid={`trash-desc-${t.id}`}>
                      <div className="truncate max-w-[380px]" title={t.human_description}>{t.human_description || "-"}</div>
                    </td>
                    <td className="px-2 py-1.5">{t.deleted_by_name || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-emerald-700">{daysLeft(t)}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex gap-1">
                        <Btn size="sm" onClick={() => restore(t)} data-testid={`trash-restore-${t.id}`}>Geri Yükle</Btn>
                        <Btn size="sm" variant="danger" onClick={() => purgeOne(t)} data-testid={`trash-purge-${t.id}`}>Kalıcı Sil</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {restoreTarget && (
        <Modal open={true} onClose={() => setRestoreTarget(null)} title="Kaydı Geri Yükle" size="md">
          <div className="space-y-3 text-sm">
            <div className="rounded-md bg-sky-50 border border-sky-200 p-3">
              <div className="text-[10px] uppercase text-slate-500 tracking-wider mb-0.5">Geri Yüklenecek Kayıt</div>
              <div className="font-semibold text-slate-800" data-testid="trash-restore-desc">{restoreTarget.human_description || "-"}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-[10px] uppercase text-slate-500 tracking-wider">Modül</div>
                <div className="font-medium">{restoreTarget.entity_name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 tracking-wider">Silinme Tarihi</div>
                <div className="font-mono">{(restoreTarget.deleted_at || "").slice(0, 19).replace("T", " ")}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 tracking-wider">Silen</div>
                <div className="font-medium">{restoreTarget.deleted_by_name || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 tracking-wider">Kalan Süre</div>
                <div className="font-mono text-emerald-700">{daysLeft(restoreTarget)}</div>
              </div>
            </div>
            {isAdmin && (
              <details className="text-[11px] text-slate-500">
                <summary className="cursor-pointer hover:text-slate-700">Teknik Detay (yalnız yönetici)</summary>
                <div className="mt-1 font-mono bg-slate-50 border border-slate-200 rounded p-2 break-all">
                  Koleksiyon: {restoreTarget.original_collection}<br/>
                  Kayıt ID: {restoreTarget.original_id}
                </div>
              </details>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="secondary" onClick={() => setRestoreTarget(null)} data-testid="trash-restore-cancel">İptal</Btn>
              <Btn onClick={confirmRestore} data-testid="trash-restore-confirm">Geri Yükle</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function WhatsAppSettings_removed_iter52() { return null; }

// ============ E-posta Sağlayıcı (SMTP) Settings ============
function EmailProviderSettings() {
  const empty = {
    enabled: false,
    provider_type: "SMTP",
    from_name: "Meta Pharma Kozmetik",
    from_email: "",
    reply_to: "",
    smtp_host: "",
    smtp_port: 587,
    encryption: "STARTTLS",
    username: "",
    password_env: "SMTP_PASSWORD",
  };
  const [f, setF] = useState(empty);
  const [configured, setConfigured] = useState(false);
  const [missing, setMissing] = useState([]);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/system/email-config");
      setF({ ...empty, ...(r.config || {}) });
      setConfigured(!!r.configured);
      setMissing(r.missing_reasons || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/system/email-config", { ...f, smtp_port: parseInt(f.smtp_port, 10) || 587 });
      setConfigured(!!r.configured);
      setMissing(r.missing_reasons || []);
      if (r.configured) {
        alert("✓ E-posta sağlayıcı kaydedildi ve etkinleştirildi.");
      } else {
        // iter299 — Belirsiz "toggle açın" mesajı yerine gerçek eksikleri göster.
        const lines = (r.missing_reasons || ["Bilinmeyen eksiklik"]).map((m, i) => `${i + 1}. ${m}`).join("\n");
        alert(`Ayarlar kaydedildi ancak servis aktif değil.\n\nEksikler:\n${lines}`);
      }
    } catch (e) { alert(e.response?.data?.detail || "Kayıt başarısız."); }
    finally { setBusy(false); }
  };

  const testSend = async () => {
    if (!testTo.trim()) { alert("Test için alıcı e-posta adresi girin."); return; }
    setBusy(true);
    try {
      const r = await api.post("/system/email-config/test", { to: testTo.trim() });
      alert(r.ok ? "✓ Test e-postası gönderildi." : `Hata: ${r.error || "Bilinmeyen hata"}`);
    } catch (e) { alert(e.response?.data?.detail || "Test e-postası gönderilemedi."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="email-provider-settings">
      <Card className={`p-4 border ${configured ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/50"}`}>
        <div className="flex items-center gap-3">
          <Mail size={22} className={configured ? "text-emerald-600" : "text-amber-600"}/>
          <div className="flex-1">
            <div className="text-sm font-semibold">
              {configured
                ? <span className="text-emerald-800">✓ E-posta servisi aktif</span>
                : <span className="text-amber-800">⚠ E-posta servisi henüz yapılandırılmamıştır</span>}
            </div>
            <div className="text-xs text-slate-500">
              {configured
                ? "PO ve Teklif PDF'lerini müşteri/tedarikçiye e-postayla gönderebilirsiniz."
                : "Aşağıdaki SMTP alanlarını doldurup Aktif toggle'ını açın. Şifre backend/.env dosyasında saklanır."}
            </div>
            {!configured && missing.length > 0 && (
              <div className="mt-2 text-[11px] text-amber-900 bg-amber-100/70 border border-amber-200 rounded p-2" data-testid="email-missing-reasons">
                <div className="font-semibold mb-1">Eksik olan noktalar:</div>
                <ul className="list-disc ml-4 space-y-0.5">
                  {missing.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-4" data-testid="email-provider-form">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Mail size={14} className="text-fuchsia-600"/> {(f.provider_type || "SMTP") === "RESEND" ? "Resend Yapılandırması" : "SMTP Yapılandırması"}</div>
        {(f.provider_type || "SMTP") === "RESEND" && (
          <div className="mb-3 text-[11px] p-2 rounded border border-emerald-300 bg-emerald-50 text-emerald-900" data-testid="email-resend-hint">
            <b>Resend seçildi.</b> Anahtar ve mail sunucusu Emergent tarafından yönetiliyor. Aşağıdaki alanlardan
            yalnızca <b>Gönderen Adı</b>, <b>Yanıt (Reply-To) Adresi</b> ve <b>Aktif</b> değerleri kullanılır.
            E-posta, Emergent'in doğrulanmış shared domain'inden gönderilir; alıcı <b>Reply-To</b> ile
            <b> {f.reply_to || f.from_email || "bilgi@…"}</b> adresine cevap yazar.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Select label="Aktif" value={f.enabled ? "1" : "0"} onChange={e => setF({ ...f, enabled: e.target.value === "1" })} data-testid="email-enabled">
            <option value="0">Pasif</option>
            <option value="1">Aktif</option>
          </Select>
          <Select label="Sağlayıcı Türü" value={f.provider_type || "SMTP"} onChange={e => setF({ ...f, provider_type: e.target.value })} data-testid="email-provider-type">
            <option value="RESEND">Resend (Emergent-managed) ★ Önerilen</option>
            <option value="SMTP">SMTP (kendi mail sunucunuz)</option>
          </Select>
          <Input label="Gönderen Adı" value={f.from_name || ""} onChange={e => setF({ ...f, from_name: e.target.value })} placeholder="Meta Pharma Kozmetik" data-testid="email-from-name"/>
          <Input label="Gönderen E-posta *" value={f.from_email || ""} onChange={e => setF({ ...f, from_email: e.target.value })} placeholder="satinalma@metapharma.com.tr" data-testid="email-from-email"/>
          <Input label="Yanıt Adresi (Reply-To)" value={f.reply_to || ""} onChange={e => setF({ ...f, reply_to: e.target.value })} placeholder="opsiyonel" data-testid="email-reply-to"/>
          <div/>
          <Input label="SMTP Sunucu *" value={f.smtp_host || ""} onChange={e => setF({ ...f, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="email-smtp-host" disabled={(f.provider_type||"SMTP")==="RESEND"}/>
          <Input label="SMTP Port *" type="number" value={f.smtp_port || 587} onChange={e => setF({ ...f, smtp_port: e.target.value })} placeholder="587" data-testid="email-smtp-port" disabled={(f.provider_type||"SMTP")==="RESEND"}/>
          <Select label="Şifreleme" value={f.encryption || "STARTTLS"} onChange={e => setF({ ...f, encryption: e.target.value })} data-testid="email-encryption" disabled={(f.provider_type||"SMTP")==="RESEND"}>
            <option value="STARTTLS">STARTTLS (port 587)</option>
            <option value="SSL">SSL (port 465)</option>
            <option value="NONE">Yok (test amaçlı)</option>
          </Select>
          <Input label="Kullanıcı Adı" value={f.username || ""} onChange={e => setF({ ...f, username: e.target.value })} placeholder="genelde e-posta ile aynı" data-testid="email-username" disabled={(f.provider_type||"SMTP")==="RESEND"}/>
          <div className="col-span-2">
            <Input label="Şifre Ortam Değişkeni Adı *"
              value={f.password_env || ""}
              onChange={e => setF({ ...f, password_env: e.target.value })}
              placeholder="SMTP_PASSWORD"
              data-testid="email-password-env"
              disabled={(f.provider_type||"SMTP")==="RESEND"}/>
            <div className="text-[11px] text-slate-500 mt-1 italic">
              Güvenlik: Şifre veritabanına yazılmaz. Bu alana <b>backend/.env</b> dosyasındaki değişken adını yazın (ör. <code>SMTP_PASSWORD</code>). Sunucuda <code>SMTP_PASSWORD=xxx</code> tanımlı olmalı.
            </div>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Btn onClick={save} disabled={busy} data-testid="email-save"><ShieldCheck size={12}/> Kaydet</Btn>
        </div>
      </Card>

      <Card className="p-4" data-testid="email-test-card">
        <div className="text-sm font-semibold mb-2">Test E-postası Gönder</div>
        <p className="text-xs text-slate-500 mb-3">Ayarlar kaydedildikten sonra bir test adresine boş PDF ekli test mesajı gönderilir.</p>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Input label="Test Alıcı E-posta" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="ornek@firma.com" data-testid="email-test-to"/>
          </div>
          <Btn onClick={testSend} disabled={busy || !configured} data-testid="email-test-send"><Send size={12}/> Test Gönder</Btn>
        </div>
        {!configured && <div className="text-[11px] text-amber-700 mt-2 italic">Servisi test etmek için önce Aktif olarak kaydedin.</div>}
      </Card>
    </div>
  );
}

export function DocumentsPage() {
  return (
    <div>
      <PageHeader title="Belgeler" description="Spesifikasyon, SDS, COA, artwork ve QC belgeleri" />
      <Card className="p-8 text-center text-sm text-slate-500">
        Belge yükleme özelliği bir sonraki sürümde tam entegre edilecektir.<br />
        (Object storage entegrasyonu ile birlikte)
      </Card>
    </div>
  );
}

// ============ Telegram Settings ============
function TelegramSettings({ isAdmin }) {
  const [status, setStatus] = useState(null);
  const [linkCode, setLinkCode] = useState(null);
  const [codes, setCodes] = useState([]);
  const [subs, setSubs] = useState({ users: [], customers: [] });
  const [outbox, setOutbox] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testBody, setTestBody] = useState("Test mesajı — Meta Pharma ERP Telegram bağlantı testi");
  const [customers, setCustomers] = useState([]);
  const [custId, setCustId] = useState("");

  const load = async () => {
    try { setStatus(await api.get("/telegram/status")); } catch {}
    if (isAdmin) {
      try { setCodes(await api.get("/telegram/link/codes")); } catch {}
      try { setSubs(await api.get("/telegram/subscribers")); } catch {}
      try { setOutbox(await api.get("/telegram/outbox?limit=25")); } catch {}
      try { setCustomers(await api.get("/customers")); } catch {}
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const generateCode = async () => {
    try { const r = await api.post("/telegram/link/request"); setLinkCode(r); load(); }
    catch (e) { alert(e.response?.data?.detail || "Kod oluşturulamadı"); }
  };
  const generateCustomerCode = async () => {
    if (!custId) return alert("Müşteri seçin");
    try { const r = await api.post("/telegram/link/customer", { customer_id: custId }); setLinkCode(r); load(); }
    catch (e) { alert(e.response?.data?.detail || "Kod oluşturulamadı"); }
  };
  const setWebhook = async () => {
    try { const r = await api.post("/telegram/admin/set-webhook"); alert(r.ok ? "✓ Webhook ayarlandı" : `Hata: ${r.description || JSON.stringify(r)}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Webhook ayarlanamadı"); }
  };
  const testSend = async () => {
    if (!testTo || !testBody) return;
    try { const r = await api.post("/telegram/test-send", { chat_id: testTo, text: testBody }); alert(r.ok ? "✓ Mesaj gönderildi" : `Hata: ${JSON.stringify(r)}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Gönderilemedi"); }
  };
  const unlink = async () => {
    if (!window.confirm("Telegram bağlantınız kaldırılsın mı?")) return;
    try { await api.post("/telegram/unlink"); load(); } catch (e) { alert(e.response?.data?.detail); }
  };

  const bot = status?.bot;
  const hook = status?.webhook;
  const me = status?.me_link;

  return (
    <div className="space-y-4" data-testid="telegram-settings">
      {!status?.configured && (
        <Card className="p-4 border-amber-300 bg-amber-50 text-sm text-amber-800">
          ⚠ TELEGRAM_BOT_TOKEN <code>backend/.env</code>'de tanımlı değil. Yönetici .env'e ekleyip backend'i yeniden başlatmalıdır.
        </Card>
      )}
      {status?.configured && (
        <Card className="p-4 bg-sky-50/70 dark:bg-sky-900/20 border-sky-200 dark:border-sky-500/30" data-testid="telegram-bot-info">
          <div className="flex items-center gap-3">
            <Send size={22} className="text-sky-600"/>
            <div className="flex-1">
              <div className="text-sm font-semibold text-sky-900 dark:text-sky-300">{bot?.first_name || "Bot"} <span className="text-slate-400">@{bot?.username}</span></div>
              <div className="text-xs text-slate-500">Bot ID: {bot?.id} · Webhook: {hook?.url ? <span className="text-emerald-600">✓ Aktif</span> : <span className="text-red-500">Ayarlanmadı</span>}</div>
              {hook?.url && <div className="text-[10px] font-mono text-slate-400 break-all">{hook.url}</div>}
            </div>
            {isAdmin && <Btn size="sm" variant="secondary" onClick={setWebhook} data-testid="telegram-set-webhook"><RefreshCw size={12}/> Webhook Yenile</Btn>}
          </div>
        </Card>
      )}

      <Card className="p-4" data-testid="telegram-my-link">
        <div className="text-sm font-semibold mb-2 flex items-center gap-2"><MessageCircle size={14} className="text-sky-600"/> Kendi Hesabımı Bağla (ERP Q&A)</div>
        {me?.chat_id ? (
          <div className="flex items-center gap-3 text-sm">
            <Badge level="ok">✓ Bağlı</Badge>
            <span className="text-slate-600">Chat ID: <code className="text-xs">{me.chat_id}</code></span>
            <Btn size="sm" variant="danger" onClick={unlink}>Bağlantıyı Kaldır</Btn>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Bir kod oluşturun ve Telegram'da <b>@{bot?.username}</b> botuna <code>/link KOD</code> yazın.</p>
            <Btn onClick={generateCode} data-testid="telegram-gen-code"><KeyRound size={12}/> Bağlantı Kodu Oluştur</Btn>
            {linkCode && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 rounded p-3 text-sm">
                <div className="font-mono text-2xl font-bold text-emerald-700 tracking-widest text-center py-1">{linkCode.code}</div>
                {linkCode.deep_link && <a href={linkCode.deep_link} target="_blank" rel="noreferrer" className="block text-center text-xs text-sky-700 hover:underline mt-1">→ Telegram'da bağla (tek tıkla)</a>}
                <div className="text-[10px] text-slate-500 text-center mt-1">Kod 1 kez kullanılır. Telegram'a: <code>/link {linkCode.code}</code></div>
              </div>
            )}
          </div>
        )}
      </Card>

      {isAdmin && (
        <>
          <Card className="p-4" data-testid="telegram-customer-link">
            <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Send size={14} className="text-emerald-600"/> Müşteriye Bağlantı Kodu Ver</div>
            <p className="text-xs text-slate-500 mb-2">Müşteri Telegram'a bağlandığında sipariş güncellemeleri (üretim tamam, kargolandı) otomatik gönderilir.</p>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Select label="Müşteri" value={custId} onChange={e => setCustId(e.target.value)} data-testid="telegram-cust-select">
                  <option value="">— Seçin —</option>
                  {customers.filter(c => c.active !== false).map(c => <option key={c.id} value={c.id}>{c.legal_name}{c.telegram_chat_id ? " (bağlı)" : ""}</option>)}
                </Select>
              </div>
              <Btn onClick={generateCustomerCode} disabled={!custId} data-testid="telegram-gen-cust-code"><KeyRound size={12}/> Kod Oluştur</Btn>
            </div>
          </Card>

          <Card className="p-4" data-testid="telegram-test-send">
            <div className="text-sm font-semibold mb-2">Test Mesajı Gönder</div>
            <div className="grid grid-cols-2 gap-2">
              <Input label="Chat ID" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="ör: 123456789" data-testid="telegram-test-to"/>
              <div/>
              <div className="col-span-2"><Textarea label="Mesaj" value={testBody} onChange={e => setTestBody(e.target.value)} rows={2} data-testid="telegram-test-body"/></div>
            </div>
            <div className="mt-2"><Btn onClick={testSend} data-testid="telegram-test-send-btn"><Send size={12}/> Gönder</Btn></div>
          </Card>

          <Card className="p-4">
            <div className="text-sm font-semibold mb-2">Bağlı Kullanıcı ve Müşteriler</div>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <div className="font-semibold text-slate-600 mb-1">Kullanıcılar ({subs.users?.length || 0})</div>
                <table className="w-full">
                  <tbody>
                    {(subs.users || []).map(u => (
                      <tr key={u.id} className="border-b border-slate-100 dark:border-slate-700/40"><td className="py-1">{u.name}</td><td className="text-slate-500 py-1">{u.role}</td><td className="font-mono text-[10px] py-1">{u.telegram_chat_id}</td></tr>
                    ))}
                    {(subs.users || []).length === 0 && <tr><td className="text-slate-400 italic py-2">Henüz yok</td></tr>}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="font-semibold text-slate-600 mb-1">Müşteriler ({subs.customers?.length || 0})</div>
                <table className="w-full">
                  <tbody>
                    {(subs.customers || []).map(c => (
                      <tr key={c.id} className="border-b border-slate-100 dark:border-slate-700/40"><td className="py-1">{c.legal_name}</td><td className="font-mono text-[10px] py-1">{c.telegram_chat_id}</td></tr>
                    ))}
                    {(subs.customers || []).length === 0 && <tr><td className="text-slate-400 italic py-2">Henüz yok</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>

          {codes.length > 0 && (
            <Card className="p-4">
              <div className="text-sm font-semibold mb-2">Bekleyen Kodlar ({codes.length})</div>
              <table className="w-full text-xs">
                <thead className="bg-slate-100 dark:bg-slate-800"><tr><th className="text-left px-2 py-1">Kod</th><th className="text-left px-2 py-1">Hedef</th><th className="text-left px-2 py-1">Tür</th><th className="text-left px-2 py-1">Oluşturma</th></tr></thead>
                <tbody>{codes.map(c => (<tr key={c.code} className="border-t border-slate-100 dark:border-slate-700/40"><td className="px-2 py-1 font-mono font-bold text-emerald-700">{c.code}</td><td className="px-2 py-1">{c.target_label}</td><td className="px-2 py-1">{c.kind}</td><td className="px-2 py-1 text-slate-400 text-[10px]">{c.created_at?.slice(0, 16).replace("T", " ")}</td></tr>))}</tbody>
              </table>
            </Card>
          )}

          <Card className="p-4">
            <div className="text-sm font-semibold mb-2">Son Giden Mesajlar</div>
            <div className="max-h-64 overflow-auto text-xs">
              {outbox.length === 0 && <div className="text-slate-400 italic py-2">Henüz mesaj yok</div>}
              {outbox.map((o, i) => (
                <div key={i} className="border-b border-slate-100 dark:border-slate-700/40 py-1.5 flex gap-2">
                  <span className="w-4">{o.ok ? "✓" : "✗"}</span>
                  <span className="w-24 font-mono text-[10px] text-slate-500">{o.kind}</span>
                  <span className="w-32 font-mono text-[10px] text-slate-500">{o.chat_id}</span>
                  <span className="flex-1 truncate">{o.text}</span>
                  <span className="text-[10px] text-slate-400">{o.created_at?.slice(11, 19)}</span>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

